import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Compass, ChevronLeft, ChevronDown, ChevronUp, Loader2, Check, Star, Play, Trophy, Sparkles, User, FileText, BookOpen, Award, Lock, Mic, Volume2, X, ArrowRight, Square, Plus, Minus, RotateCcw, SkipForward, AlertCircle, Languages, Bookmark, CheckCircle2, ArrowLeftRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { textToSpeechService } from '../services/textToSpeechService';
import ReactMarkdown from 'react-markdown';
import { getLocalISOString } from '../utils/timezoneUtils';
import { useData } from '../contexts/DataContext';
import { useStreakCounter } from '../hooks/useStreakCounter';
import { streakService } from '../services/streakService';
import { ROADMAP_BONUS_DEFS, PRO_REQUIRED_MESSAGES, BonusTopicDef } from '../constants/roadmapBonusData';
import BonusActiveVoiceModal from '../components/BonusActiveVoiceModal';
import LevelCompletionModal from '../components/LevelCompletionModal';
import { TopStatsBar } from '../components/TopStatsBar';
import ErrorReviewFloatingButton from '../components/ErrorReviewFloatingButton';
import { GrammarChoiceScreen, GrammarVideoPlayer } from '../components/GrammarVideoPlayer';
import { NEW_GRAMMAR_LANGUAGES, GRAMMAR_VIDEO_LANGS } from '../constants/grammarLanguages';
import { translationService } from '../services/translationService';
import { translateQuizPrompt, translateQuizExplanation } from '../services/quizTranslationService';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import T from '../components/T';
import i18nInstance from '../i18n';

const LOCAL_TRANSLATIONS: Record<string, any> = {
  pt: {
    grammarWorkingOnIt: "Estamos trabalhando nisso, o conteúdo será adicionado em breve.",
    translatePage: "Traduzir página",
    errorLoading: "Não foi possível carregar a explicação.",
    goldenTip: "Dica de Ouro",
    next: "Próximo",
    startQuiz: "Iniciar Quiz",
    correctFeedback: "Correto!",
    incorrectFeedback: "Incorreto!",
    continueBtn: "Continuar",
    excellentResult: "Excelente! 🎉",
    keepPracticingResult: "Continue Praticando! 💪",
    correctAnswersCount: "Acertos",
    backToTrack: "Voltar para Trilha",
    scoreWarning: "Você precisa tirar nota 7 para concluir este módulo.",
    back: "Voltar",
    congratsTitle: "PARABÉNS! 🏆",
    congratsText: "Você concluiu todos os exercícios do Tópico {num} com maestria e desbloqueou o próximo nível do seu roadmap!",
    uauContinue: "Uau, Continuar!",
    tryAgain: "Tentar Novamente",
    minScoreWarning: "Você precisa acertar pelo menos 7 questões para concluir o tópico.",
    topicWelcomeTitle: "Bem-vindo ao Tópico {num}",
    topicWelcomeIntro: "Neste tópico você vai praticar e consolidar seu aprendizado!",
    topicWelcomeGrammarIntro: "Neste tópico você vai aprender um pouco mais sobre gramática e concluir exercícios!",
    topicContainsGrammar: "Explicação gramatical e lição didática",
    topicContainsQuiz: "10 testes de fixação (Quiz)",
    topicContainsPronunciation: "5 exercícios para testar sua pronúncia",
    topicContainsFlashcards: "5 flashcards para ajudar na fixação do idioma",
    topicContainsWriting: "1 exercício de escrita para praticar tradução",
    confirm: "Confirmar",
    skip: "Pular",
    blockedBonusTitle: "Tópico Bônus Bloqueado!",
    blockedBonusMsg: "Complete os 10 tópicos anteriores do roadmap para desbloquear este bônus.",
    blockedTopicTitle: "Tópico Bloqueado!",
    blockedTopicMsg: "Complete os tópicos anteriores para desbloquear.",
    errorExplanation: "Explicação do erro:",
    premiumLimitTitle: "Limite atingido",
    premiumLimitMessage: "Você atingiu o limite de correções diárias. Faça upgrade para o LibriTui Premium para correções ilimitadas com o Professor!",
    close: "Fechar",
    freeCorrectionsLabel: "Correções gratuitas diárias: {count}/5",
    motivationalPhrases: [
      "Parabéns! Tópico quase concluído, está indo bem!",
      "Excelente progresso! Continue assim e você dominará o idioma!",
      "Você está se saindo muito bem! Vamos para a próxima etapa!",
      "Incrível! Seu esforço está valendo a pena!",
      "Muito bom! Passo a passo você chega lá!",
      "Ótimo trabalho! Mantenha o foco e continue brilhando!",
      "Sensacional! Seu aprendizado está decolando!",
      "Perfeito! Cada etapa concluída te deixa mais perto da fluência!",
      "Parabéns pelo empenho! Você está evoluindo rápido!",
      "Maravilhoso! Vamos continuar com essa energia incrível!"
    ],
    flashcardIntros: [
      "Agora faremos exercícios de flashcards. É importante que você tente traduzir ou entender o contexto da frase antes de clicar para ver a tradução. Este exercício a longo prazo ajuda a aprender e memorizar vocabulário no idioma de forma natural. Vamos lá!",
      "Bem-vindo aos flashcards! Tente se lembrar do significado de cada palavra antes de revelar a resposta. A repetição espaçada é uma das melhores técnicas para fixar vocabulário na memória de longo prazo!",
      "Preparado para os flashcards? Olhe para a palavra ou frase, faça um esforço mental para traduzir e depois confira. Esse esforço é o que fortalece as conexões neurais do aprendizado!"
    ],
    pronunciationIntros: [
      "Hora de praticar sua pronúncia! Falar em voz alta é essencial para desenvolver a musculatura da fala e ganhar confiança. Escute o áudio com atenção, clique no microfone e repita a frase. Não tenha medo de errar, a prática leva à perfeição!",
      "Vamos treinar a fala agora! A pronúncia correta vem com a escuta atenta e a imitação. Ouça o exemplo, grave a sua voz e observe o feedback. Cada tentativa te deixa mais próximo da fluência!",
      "Próximo passo: Pronúncia! A melhor maneira de perder o medo de falar é praticando desde o início. Ajuste o microfone, respire fundo e repita a frase que ouvir. Você consegue!"
    ],
    grammarIntros: [
      "Aprenderemos agora um pouco de gramática. Compreender a estrutura do idioma te dá a liberdade de construir suas próprias frases com segurança. Leia a explicação didática com atenção e, ao concluir, faremos um quiz para testar seu entendimento!",
      "Vamos mergulhar nas regras gramaticais! A gramática é o esqueleto do idioma. Entender como as frases são formadas vai acelerar muito o seu aprendizado. Leia os exemplos práticos e depois responda ao quiz de fixação!",
      "Hora de entender a lógica do idioma! A gramática nos ajuda a organizar as palavras de forma coerente. Preste atenção na lição a seguir e garanta que entendeu fazendo o questionário logo depois. Boa sorte!"
    ],
    writingIntros: [
      "O objetivo deste exercício é desenvolver sua escrita e estruturação de frases no idioma. Traduzir textos ajuda a consolidar o vocabulário e a gramática de forma ativa. Escreva sua resposta e receba o feedback detalhado do professor de IA!",
      "Prática de escrita! Escrever à mão ou digitar no novo idioma força seu cérebro a recuperar ativamente as palavras e regras aprendidas. Faça o seu melhor na tradução e veja as sugestões de melhoria!",
      "Agora vamos praticar a escrita! Este exercício é excelente para lapidar sua precisão gramatical e vocabulário. Traduza o texto proposto e nosso professor de IA ajudará a aperfeiçoar seu estilo!"
    ],
    activeVoiceIntros: [
      "Vamos praticar a voz ativa! Converter frases ou expressar ações diretamente ajuda a tornar sua comunicação mais clara, dinâmica e natural no dia a dia. Vamos começar!",
      "Treinamento de Voz Ativa! Focar em quem realiza a ação torna suas frases mais diretas e fáceis de entender. Pratique a estruturação ativa e ganhe confiança na fala e escrita!",
      "Pronto para exercitar a voz ativa? Este exercício vai te ajudar a soar mais natural e dinâmico ao conversar no novo idioma. Preste atenção nos exemplos e pratique!"
    ]
  },
  en: {
    grammarWorkingOnIt: "We are working on this, the content will be added soon.",
    translatePage: "Translate page",
    errorLoading: "Could not load the explanation.",
    goldenTip: "Golden Tip",
    next: "Next",
    startQuiz: "Start Quiz",
    correctFeedback: "Correct!",
    incorrectFeedback: "Incorrect!",
    continueBtn: "Continue",
    excellentResult: "Excellent! 🎉",
    keepPracticingResult: "Keep Practicing! 💪",
    correctAnswersCount: "Correct Answers",
    backToTrack: "Back to Track",
    scoreWarning: "You need to score 7 to complete this module.",
    back: "Back",
    congratsTitle: "CONGRATULATIONS! 🏆",
    congratsText: "You successfully completed all exercises for Topic {num} and unlocked the next level of your roadmap!",
    uauContinue: "Wow, Continue!",
    tryAgain: "Try Again",
    minScoreWarning: "You need to answer at least 7 questions correctly to complete the topic.",
    topicWelcomeTitle: "Welcome to Topic {num}",
    topicWelcomeIntro: "In this topic you will practice and consolidate your learning!",
    topicWelcomeGrammarIntro: "In this topic you will learn a bit more about grammar and complete exercises!",
    topicContainsGrammar: "Grammar explanation and didactical lesson",
    topicContainsQuiz: "10 consolidation tests (Quiz)",
    topicContainsPronunciation: "5 exercises to test your pronunciation",
    topicContainsFlashcards: "5 flashcards to help language recall",
    topicContainsWriting: "1 writing exercise to practice translation",
    confirm: "Confirm",
    skip: "Skip",
    blockedBonusTitle: "Bonus Topic Locked!",
    blockedBonusMsg: "Complete the 10 previous topics in the roadmap to unlock this bonus.",
    blockedTopicTitle: "Topic Locked!",
    blockedTopicMsg: "Complete the previous topics to unlock.",
    errorExplanation: "Error explanation:",
    premiumLimitTitle: "Limit reached",
    premiumLimitMessage: "You have reached your daily correction limit. Upgrade to LibriTui Premium for unlimited corrections with the Teacher!",
    close: "Close",
    freeCorrectionsLabel: "Daily free corrections: {count}/5",
    motivationalPhrases: [
      "Congratulations! Topic almost completed, you are doing well!",
      "Excellent progress! Keep it up and you will master the language!",
      "You are doing great! Let's go to the next stage!",
      "Amazing! Your effort is paying off!",
      "Very good! Step by step you get there!",
      "Great work! Keep focus and keep shining!",
      "Sensational! Your learning is taking off!",
      "Perfect! Each completed stage gets you closer to fluency!",
      "Congratulations on your commitment! You are evolving fast!",
      "Wonderful! Let's continue with this amazing energy!"
    ],
    flashcardIntros: [
      "Now we will do flashcard exercises. It is important that you try to translate or understand the sentence context before clicking to see the translation. In the long term, this exercise helps you learn and memorize vocabulary naturally. Let's go!",
      "Welcome to flashcards! Try to remember the meaning of each word before revealing the answer. Spaced repetition is one of the best techniques to fix vocabulary in long-term memory!",
      "Ready for flashcards? Look at the word or phrase, make a mental effort to translate, and then check. This effort is what strengthens learning neural connections!"
    ],
    pronunciationIntros: [
      "Time to practice your pronunciation! Speaking out loud is essential to develop speech muscles and gain confidence. Listen to the audio carefully, click the microphone, and repeat. Don't be afraid to make mistakes, practice makes perfect!",
      "Let's practice speaking now! Proper pronunciation comes from active listening and imitation. Listen to the example, record your voice, and look at the feedback. Every attempt brings you closer to fluency!",
      "Next step: Pronunciation! The best way to overcome the fear of speaking is to practice from the start. Adjust the microphone, take a deep breath, and repeat. You can do it!"
    ],
    grammarIntros: [
      "Now we will learn some grammar. Understanding the language structure gives you the freedom to build your own sentences with confidence. Read the lesson explanation carefully, and then we will do a quiz to test your understanding!",
      "Let's dive into grammar rules! Grammar is the skeleton of the language. Understanding how sentences are formed will greatly speed up your learning. Read the practical examples and then answer the quiz!",
      "Time to understand the language's logic! Grammar helps us organize words coherently. Pay attention to the following lesson and make sure you understand it by completing the quiz afterward. Good luck!"
    ],
    writingIntros: [
      "The goal of this exercise is to develop your writing and sentence structure in the language. Translating texts helps consolidate vocabulary and grammar actively. Write your answer and receive detailed feedback from the AI teacher!",
      "Writing practice! Writing by hand or typing in the new language forces your brain to actively retrieve learned words and rules. Do your best in the translation and see the improvement suggestions!",
      "Now let's practice writing! This exercise is excellent for refining your grammatical precision and vocabulary. Translate the proposed text and our AI teacher will help improve your style!"
    ],
    activeVoiceIntros: [
      "Let's practice the active voice! Converting sentences or expressing actions directly helps make your communication clearer, more dynamic, and natural in daily life. Let's start!",
      "Active Voice training! Focusing on who performs the action makes your sentences more direct and easier to understand. Practice active structuring and gain confidence in speaking and writing!",
      "Ready to exercise active voice? This exercise will help you sound more natural and dynamic when speaking the new language. Pay attention to the examples and practice!"
    ]
  },
  es: {
    grammarWorkingOnIt: "Estamos trabajando en ello, el contenido se agregará pronto.",
    translatePage: "Traducir página",
    errorLoading: "No se pudo cargar la explicación.",
    goldenTip: "Consejo de oro",
    next: "Siguiente",
    startQuiz: "Iniciar cuestionario",
    correctFeedback: "¡Correcto!",
    incorrectFeedback: "¡Incorrecto!",
    continueBtn: "Continuar",
    excellentResult: "¡Excelente! 🎉",
    keepPracticingResult: "¡Sigue practicando! 💪",
    correctAnswersCount: "Aciertos",
    backToTrack: "Volver a la ruta",
    scoreWarning: "Necesitas obtener una puntuación de 7 para completar este módulo.",
    back: "Volver",
    congratsTitle: "¡FELICITACIONES! 🏆",
    congratsText: "¡Completaste con éxito todos los ejercicios del Tema {num} y desbloqueaste el siguiente nivel de tu roadmap!",
    uauContinue: "¡Guau, Continuar!",
    tryAgain: "Intentar otra vez",
    minScoreWarning: "Debes responder correctamente al menos 7 preguntas para completar el tema.",
    topicWelcomeTitle: "Bienvenido al Tema {num}",
    topicWelcomeIntro: "¡En este tema practicarás y consolidarás tu aprendizaje!",
    topicWelcomeGrammarIntro: "¡En este tema aprenderás un poco más sobre gramática y completarás ejercicios!",
    topicContainsGrammar: "Explicación gramatical y lección didáctica",
    topicContainsQuiz: "10 pruebas de consolidación (Cuestionario)",
    topicContainsPronunciation: "5 ejercicios para probar tu pronunciación",
    topicContainsFlashcards: "5 flashcards para ayudar a fijar el idioma",
    topicContainsWriting: "1 ejercicio de escritura para practicar traducción",
    confirm: "Confirmar",
    skip: "Saltar",
    blockedBonusTitle: "¡Tema Bonus Bloqueado!",
    blockedBonusMsg: "Completa los 10 temas anteriores del roadmap para desbloquear este bonus.",
    blockedTopicTitle: "¡Tema Bloqueado!",
    blockedTopicMsg: "Completa los temas anteriores para desbloquear.",
    errorExplanation: "Explicación del error:",
    premiumLimitTitle: "Límite alcanzado",
    premiumLimitMessage: "Has alcanzado el límite de correcciones diarias. ¡Actualiza a LibriTui Premium para correcciones ilimitadas con el Profesor!",
    close: "Cerrar",
    freeCorrectionsLabel: "Correcciones gratuitas diarias: {count}/5",
    motivationalPhrases: [
      "¡Felicitaciones! ¡Tema casi terminado, vas muy bien!",
      "¡Excelente progreso! ¡Sigue así y dominarás el idioma!",
      "¡Lo estás haciendo genial! ¡Vamos al siguiente paso!",
      "¡Increíble! ¡Tu esfuerzo está dando sus frutos!",
      "¡Muy bien! ¡Paso a paso llegarás allí!",
      "¡Buen trabajo! ¡Mantén el enfoque y sigue brillando!",
      "¡Sensacional! ¡Tu aprendizaje está despegando!",
      "¡Perfecto! ¡Cada etapa completada te acerca a la fluidez!",
      "¡Felicitaciones por tu esfuerzo! ¡Estás progresando rápido!",
      "¡Maravilloso! ¡Sigamos con esta increíble energía!"
    ],
    flashcardIntros: [
      "Ahora faremos ejercicios de flashcards. Es importante que intentes traducir o entender el contexto de la frase antes de hacer clic para ver la traducción. ¡Vamos!",
      "¡Bienvenido a las flashcards! Intenta recordar el significado de cada palabra antes de revelar la respuesta. ¡La repetición espaciada es clave!",
      "¿Preparado para las flashcards? Mira la palabra o frase, haz un esfuerzo mental por traducir y luego comprueba. ¡Eso fortalece tu aprendizaje!"
    ],
    pronunciationIntros: [
      "¡Hora de practicar tu pronunciación! Hablar en voz alta es esencial para ganar confianza. Escucha el audio con atención, haz clic en el micrófono y repite.",
      "¡Vamos a entrenar el habla ahora! La pronunciación correcta viene de la escucha atenta y la imitación. Escucha el ejemplo y graba tu voz.",
      "Siguiente paso: ¡Pronunciación! La mejor manera de perder el miedo a hablar es practicando desde el principio. ¡Ajuste el micrófono y repite la frase!"
    ],
    grammarIntros: [
      "Aprenderemos agora un poco de gramática. Comprender la estructura del idioma te da la libertad de construir tus propias frases. ¡Lee la explicación con atención!",
      "¡Vamos a sumergirnos en las reglas gramaticales! La gramática es el esqueleto del idioma. Lee los ejemplos prácticos y luego responde el cuestionario.",
      "¡Hora de entender la lógica del idioma! La gramática nos ayuda a organizar las palabras de forma coherente. ¡Presta atención a la lección y haz el quiz!"
    ],
    writingIntros: [
      "El objetivo de este ejercicio es desarrollar tu escritura en el idioma. Traducir textos ayuda a consolidar el vocabulario de forma activa. ¡Escribe tu respuesta!",
      "¡Práctica de escritura! Escribir o teclear en el nuevo idioma fuerza a tu cerebro a recuperar las palabras aprendidas. ¡Haz tu mejor esfuerzo en la traducción!",
      "¡Ahora vamos a practicar la escritura! Este ejercicio es excelente para pulir tu precisión gramatical. ¡Traduce el texto y nuestro profesor de IA te ayudará!"
    ],
    activeVoiceIntros: [
      "¡Vamos a practicar la voz activa! Convertir frases o expresar acciones directamente ayuda a que tu comunicación sea más clara y natural. ¡Comencemos!",
      "¡Entrenamiento de voz activa! Centrarse en quién realiza la acción hace que tus frases sean más directas y fáciles de entender. ¡Práctica y gana confianza!",
      "¿Listo para ejercitar la voz activa? Este ejercicio te ayudará a sonar más natural y dinámico al conversar en el nuevo idioma. ¡Presta atención a los ejemplos!"
    ]
  },
  fr: {
    grammarWorkingOnIt: "Nous y travaillons, le contenu sera bientôt ajouté.",
    translatePage: "Traduire la page",
    errorLoading: "Impossible de charger l'explication.",
    goldenTip: "Conseil en or",
    next: "Suivant",
    startQuiz: "Commencer le quiz",
    correctFeedback: "Correct !",
    incorrectFeedback: "Incorrect !",
    continueBtn: "Continuer",
    excellentResult: "Excellent ! 🎉",
    keepPracticingResult: "Continuez à pratiquer ! 💪",
    correctAnswersCount: "Bonnes réponses",
    backToTrack: "Retour au parcours",
    scoreWarning: "Vous devez obtenir un score de 7 pour compléter ce module.",
    back: "Retour",
    congratsTitle: "FÉLICITATIONS ! 🏆",
    congratsText: "Vous avez terminé avec succès tous les exercices du Thème {num} et débloqué le niveau suivant de votre roadmap !",
    uauContinue: "Super, Continuer !",
    tryAgain: "Réessayer",
    minScoreWarning: "Vous devez répondre correctement à au moins 7 questions pour terminer le thème.",
    topicWelcomeTitle: "Bienvenue au Thème {num}",
    topicWelcomeIntro: "Dans ce thème, vous allez pratiquer et consolider votre apprentissage !",
    topicWelcomeGrammarIntro: "Dans ce thème, vous en apprendrez un peu plus sur la grammaire et terminerez des exercices !",
    topicContainsGrammar: "Explication de grammaire et leçon didactique",
    topicContainsQuiz: "10 tests de consolidation (Quiz)",
    topicContainsPronunciation: "5 exercices pour tester votre prononciation",
    topicContainsFlashcards: "5 flashcards pour aider à fixer la langue",
    topicContainsWriting: "1 exercice d'écriture pour pratiquer la traduction",
    confirm: "Confirmer",
    skip: "Passer",
    blockedBonusTitle: "Sujet Bonus Verrouillé !",
    blockedBonusMsg: "Complétez les 10 sujets précédents du roadmap pour débloquer ce bonus.",
    blockedTopicTitle: "Sujet Verrouillé !",
    blockedTopicMsg: "Complétez les sujets précédents pour débloquer.",
    errorExplanation: "Explication de l'erreur :",
    premiumLimitTitle: "Limite atteinte",
    premiumLimitMessage: "Vous avez atteint votre limite de corrections quotidiennes. Passez à LibriTui Premium pour des corrections illimitées avec le professeur !",
    close: "Fermer",
    freeCorrectionsLabel: "Corrections gratuites quotidiennes : {count}/5",
    motivationalPhrases: [
      "Félicitations ! Thème presque terminé, vous vous en sortez bien !",
      "Excellent progrès ! Continuez ainsi et vous maîtriserez la langue !",
      "Vous vous en sortez très bien ! Passons à la suite !",
      "Incroyable ! Vos efforts portent leurs fruits !",
      "Très bien ! Pas à pas, vous y arriverez !",
      "Excellent travail ! Restez concentré et continuez à briller !",
      "Sensationnel ! Votre apprentissage prend son envol !",
      "Parfait ! Chaque étape franchie vous rapproche de la fluidité !",
      "Félicitations pour votre engagement ! Vous évoluez rapidement !",
      "Merveilleux ! Continuons avec cette superbe énergie !"
    ],
    flashcardIntros: [
      "Nous allons faire des exercices de flashcards. Tentez de traduire ou de comprendre le contexte de la phrase avant de cliquer. Cet exercice aide à mémoriser !",
      "Bienvenue dans les flashcards ! Essayez de vous rappeler le sens de chaque mot avant de révéler la réponse. La répétition espacée est idéale !",
      "Prêt pour les flashcards ? Regardez le mot ou la phrase, faites un effort mental pour traduire, puis vérifiez. Cela renforce les connexions du cerveau !"
    ],
    pronunciationIntros: [
      "C'est le moment de pratiquer votre prononciation ! Parler à voix haute est essentiel pour prendre confiance. Écoutez attentivement, cliquez et répétez.",
      "Entraînons-nous à parler maintenant ! Une bonne prononciation vient de l'écoute attentive et de l'imitation. Écoutez l'exemple et enregistrez votre voix.",
      "Étape suivante : Prononciation ! La meilleure façon de surmonter la peur de parler est de pratiquer dès le début. Ajustez votre micro et répétez."
    ],
    grammarIntros: [
      "Nous allons apprendre un peu de gramática. Comprendre la structure de la langue vous donne la liberté de construire vos phrases en toute sécurité.",
      "Plongeons dans les règles de grammaire ! La grammaire est le squelette de la langue. Lisez les exemples pratiques puis répondez au quiz.",
      "Il est temps de comprendre la logique de la langue ! La grammaire nous aide à organiser les mots de manière cohérente. Suivez bien la leçon."
    ],
    writingIntros: [
      "Le but de cet exercice est de développer votre écriture. Traduire des textes aide à consolider le vocabulaire de manière active. Écrivez votre réponse !",
      "Pratique de l'écriture ! Écrire ou taper dans la nouvelle langue force votre cerveau à récupérer les mots appris. Faites de votre mieux pour traduire !",
      "Pratiquons maintenant l'écriture ! Cet exercice est excellent pour affiner votre précision grammaticale. Traduisez le texte proposé."
    ],
    activeVoiceIntros: [
      "Pratiquons la voix active ! Convertir des phrases ou exprimer des actions directement rend votre communication plus claire et naturelle au quotidien.",
      "Entraînement à la voix active ! Se concentrer sur celui qui fait l'action rend vos phrases plus directes et compréhensibles. Pratiquez activement.",
      "Prêt à exercer la voix active ? Cet exercice vous aidera à paraître plus naturel et dynamique lors des conversations. Observez les exemples."
    ]
  },
  de: {
    grammarWorkingOnIt: "Wir arbeiten daran, der Inhalt wird in Kürze hinzugefügt.",
    translatePage: "Seite übersetzen",
    errorLoading: "Die Erklärung konnte nicht geladen werden.",
    goldenTip: "Goldener Tipp",
    next: "Weiter",
    startQuiz: "Quiz starten",
    correctFeedback: "Richtig!",
    incorrectFeedback: "Falsch!",
    continueBtn: "Weiter",
    excellentResult: "Hervorragend! 🎉",
    keepPracticingResult: "Übe weiter! 💪",
    correctAnswersCount: "Richtige Antworten",
    backToTrack: "Zurück zum Pfad",
    scoreWarning: "Sie müssen 7 Punkte erzielen, um dieses Modul abzuschließen.",
    back: "Zurück",
    congratsTitle: "HERZLICHEN GLÜCKWUNSCH! 🏆",
    congratsText: "Du hast alle Übungen für Thema {num} erfolgreich abgeschlossen und die nächste Stufe deiner Roadmap freigeschaltet!",
    uauContinue: "Wow, Weiter!",
    tryAgain: "Erneut versuchen",
    minScoreWarning: "Sie müssen mindestens 7 Fragen richtig beantworten, um das Thema abzuschließen.",
    topicWelcomeTitle: "Willkommen bei Thema {num}",
    topicWelcomeIntro: "In diesem Thema wirst du dein Gelerntes praktizieren und festigen!",
    topicWelcomeGrammarIntro: "In diesem Thema wirst du mehr über Grammatik lernen und Übungen abschließen!",
    topicContainsGrammar: "Grammatikerklärung und didaktische Lektion",
    topicContainsQuiz: "10 Festigungsprüfungen (Quiz)",
    topicContainsPronunciation: "5 Übungen zum Testen deiner Aussprache",
    topicContainsFlashcards: "5 Karteikarten zur Festigung der Sprache",
    topicContainsWriting: "1 Schreibübung zur Übersetzungspraxis",
    confirm: "Bestätigen",
    skip: "Überspringen",
    blockedBonusTitle: "Bonus-Thema Gesperrt!",
    blockedBonusMsg: "Schließe die 10 vorherigen Themen der Roadmap ab, um diesen Bonus freizuschalten.",
    blockedTopicTitle: "Thema Gesperrt!",
    blockedTopicMsg: "Schließe die vorherigen Themen ab, um sie freizuschalten.",
    errorExplanation: "Fehlererklärung:",
    premiumLimitTitle: "Limit erreicht",
    premiumLimitMessage: "Sie haben Ihr tägliches Korrekturlimit erreicht. Aktualisieren Sie auf LibriTui Premium für unbegrenzte Korrekturen durch den Lehrer!",
    close: "Schließen",
    freeCorrectionsLabel: "Tägliche kostenlose Korrekturen: {count}/5",
    motivationalPhrases: [
      "Herzlichen Glückwunsch! Thema fast abgeschlossen, du machst das super!",
      "Hervorragender Fortschritt! Mach weiter so und du wirst die Sprache meistern!",
      "Du machst das großartig! Auf zur nächsten Stufe!",
      "Unglaublich! Dein Einsatz zahlt sich aus!",
      "Sehr gut! Schritt für Schritt kommst du ans Ziel!",
      "Tolle Arbeit! Bleib fokussiert und glänze weiter!",
      "Sensationell! Dein Lernen nimmt richtig Fahrt auf!",
      "Perfekt! Jede abgeschlossene Stufe bringt dich der Fließendheit näher!",
      "Glückwunsch zu deinem Engagement! Du entwickelst dich schnell!",
      "Wunderbar! Weiter so mit dieser tollen Energie!"
    ],
    flashcardIntros: [
      "Jetzt machen wir Karteikarten-Übungen. Versuche, den Kontext des Satzes zu übersetzen oder zu verstehen, bevor du klickst. Das hilft dir, Vokabeln auf natürliche Weise zu lernen. Los geht's!",
      "Willkommen bei den Karteikarten! Versuche, dich an die Bedeutung jedes Wortes zu erinnern, bevor du die Antwort aufdeckst. Spaced Repetition ist eine der besten Techniken!",
      "Bereit für Karteikarten? Sieh dir das Wort oder den Satz an, mache eine mentale Anstrengung und überprüfe es dann. Das stärkt die Lernverbindungen!"
    ],
    pronunciationIntros: [
      "Zeit, deine Aussprache zu üben! Lautes Sprechen ist wichtig, um die Sprechmuskulatur zu entwickeln. Höre genau hin, klicke auf das Mikrofon und wiederhole den Satz. Hab keine Angst vor Fehlern!",
      "Lass uns jetzt das Sprechen üben! Richtige Aussprache kommt durch aktives Zuhören und Nachahmen. Höre dir das Beispiel an und nimm deine Stimme auf!",
      "Nächster Schritt: Aussprache! Der beste Weg, die Aussprache zu trainieren, ist von Anfang an zu sprechen. Mikrofon einstellen und wiederholen."
    ],
    grammarIntros: [
      "Jetzt lernen wir etwas Grammatik. Die Struktur der Sprache zu verstehen, gibt dir die Freiheit, Sätze selbstbewusst zu bauen. Lies die Erklärung aufmerksam durch!",
      "Lass uns in die Grammatikregeln eintauchen! Grammatik ist das Skelett der Sprache. Lies die praktischen Beispiele und beantworte dann das Quiz!",
      "Zeit, die Logik der Sprache zu verstehen! Grammatik hilft uns, Wörter kohärent zu ordnen. Achte auf die folgende Lektion und beantworte danach das Quiz!"
    ],
    writingIntros: [
      "Das Ziel dieser Übung ist es, dein Schreiben im neuen Medium zu entwickeln. Das Übersetzen von Texten hilft dir, Vokabeln und Grammatik aktiv zu festigen!",
      "Schreibpraxis! Das Schreiben von Hand oder Tippen zwingt dein Gehirn, gelernte Wörter aktiv abzurufen. Tue dein Bestes und sieh dir die Korrekturen an!",
      "Jetzt üben wir das Schreiben! Diese Übung ist hervorragend, um deine grammatikalische Präzision zu verfeinern. Übersetze den vorgeschlagenen Text!"
    ],
    activeVoiceIntros: [
      "Lass uns das Aktiv üben! Sätze umzuwandeln oder Aktionen direkt auszudrücken, hilft, deine Kommunikation im Alltag klarer und natürlicher zu machen!",
      "Aktiv-Training! Die Konzentration darauf, wer die Aktion ausführt, macht deine Sätze direkter. Übe die aktive Strukturierung und gewinne an Vertrauen!",
      "Bereit für das Aktiv? Diese Übung hilft dir, beim Sprechen natürlicher zu klingen. Achte auf die Beispiele und übe!"
    ]
  },
  it: {
    grammarWorkingOnIt: "Ci stiamo lavorando, il contenuto verrà aggiunto presto.",
    translatePage: "Traduci la pagina",
    errorLoading: "Impossibile caricare la spiegazione.",
    goldenTip: "Consiglio d'oro",
    next: "Avanti",
    startQuiz: "Inizia il quiz",
    correctFeedback: "Correto!",
    incorrectFeedback: "Errato!",
    continueBtn: "Continua",
    excellentResult: "Eccellente! 🎉",
    keepPracticingResult: "Continua a esercitarti! 💪",
    correctAnswersCount: "Risposte corrette",
    backToTrack: "Torna al percorso",
    scoreWarning: "Devi ottenere un punteggio di 7 per completare questo modulo.",
    back: "Indietro",
    congratsTitle: "CONGRATULAZIONI! 🏆",
    congratsText: "Hai completato con successo tutti gli esercizi per il Tema {num} e sbloccato il livello successivo della tua tabella di marcia!",
    uauContinue: "Wow, Continua!",
    tryAgain: "Riprova",
    minScoreWarning: "Devi rispondere correttamente ad almeno 7 domande per completare il tema.",
    topicWelcomeTitle: "Benvenuto al Tema {num}",
    topicWelcomeIntro: "In questo tema farai pratica e consoliderai il tuo apprendimento!",
    topicWelcomeGrammarIntro: "In questo tema imparerai qualcosa in più sulla grammatica e completerai gli esercizi!",
    topicContainsGrammar: "Spiegazione della grammatica e lezione didattica",
    topicContainsQuiz: "10 test di consolidamento (Quiz)",
    topicContainsPronunciation: "5 esercizi per testare la tua pronuncia",
    topicContainsFlashcards: "5 flashcard per aiutare a fissare la lingua",
    topicContainsWriting: "1 esercizio di scrittura per praticare la traduzione",
    confirm: "Conferma",
    skip: "Salta",
    blockedBonusTitle: "Argomento Bonus Bloccato!",
    blockedBonusMsg: "Completa i 10 argomenti precedenti del roadmap per sbloccare questo bonus.",
    blockedTopicTitle: "Argomento Bloccato!",
    blockedTopicMsg: "Completa gli argomenti precedenti per sbloccare.",
    errorExplanation: "Spiegazione dell'errore:",
    premiumLimitTitle: "Limite raggiunto",
    premiumLimitMessage: "Hai raggiunto il limite di correzioni giornaliere. Passa a LibriTui Premium per correzioni illimitate con il Professore!",
    close: "Chiudi",
    freeCorrectionsLabel: "Correzioni gratuite giornaliere: {count}/5",
    motivationalPhrases: [
      "Congratulazioni! Tema quasi completato, stai andando alla grande!",
      "Eccellente progresso! Continua così e dominerai la lingua!",
      "Ti stai comportando benissimo! Andiamo alla fase successiva!",
      "Incredibile! I tuoi sforzi stanno dando i loro frutti!",
      "Molto bene! Passo dopo passo ci arriverai!",
      "Ottimo lavoro! Rimani concentrato e continua a brillare!",
      "Sensazionale! Il tuo apprendimento sta decollando!",
      "Perfetto! Ogni tappa completata ti avvicina alla fluidità!",
      "Congratulazioni per l'impegno! Stai migliorando rapidamente!",
      "Meraviglioso! Continuiamo con questa incredibile energia!"
    ],
    flashcardIntros: [
      "Ora faremo esercizi di flashcard. È importante que tu provi a tradurre o capire il contesto della frase prima di cliccare per vedere la traduzione. Andiamo!",
      "Benvenuto nelle flashcard! Prova a ricordare il significato di ogni parola prima di rivelare la risposta. La ripetizione spaziata è fantastica!",
      "Pronto per le flashcard? Guarda la parola o frase, fai uno sforzo mentale per tradurre e poi controlla. Questo sforzo rafforza l'apprendimento!"
    ],
    pronunciationIntros: [
      "È ora di praticare la tua pronuncia! Parlare ad alta voce è essenziale per sviluppare i muscoli della voce e acquisire sicurezza. Ascolta e ripeti.",
      "Alleniamo il parlato ora! La pronuncia correta deriva dall'ascolto attento e dall'imitazione. Ascolta l'esempio e registra la tua voce.",
      "Prossimo passo: Pronuncia! Il modo migliore per perdere la paura di parlare è esercitarsi fin dall'inizio. Regola il microfono e ripeti!"
    ],
    grammarIntros: [
      "Impareremo ora un pouco de gramática. Comprendere la struttura della lingua ti dà la libertà di costruire le tue frasi con sicurezza. Leggi con attenzione.",
      "Immergiamoci nelle regole grammaticali! La grammatica è lo scheletro della lingua. Leggi gli esempi pratici e poi rispondi al quiz.",
      "È ora di capire la logica della lingua! La grammatica ci aiuta a organizzare le parole in modo coerente. Presta attenzione alla lezione."
    ],
    writingIntros: [
      "L'obiettivo di questo esercizio è sviluppare la tua scrittura nella lingua. Tradurre testi aiuta a consolidare il vocabolario in modo attivo.",
      "Pratica di scrittura! Scrivere a mano o digitare nella nuova lingua costringe il cervello a recuperare le parole apprese. Fai del tuo meglio!",
      "Ora pratichiamo la scrittura! Questo esercizio è eccellente per affinare la tua precisione grammaticale. Traduci il testo proposto."
    ],
    activeVoiceIntros: [
      "Pratichiamo la voce attiva! Convertire le frasi o esprimere le azioni direttamente aiuta a rendere la comunicazione più chiara e naturale.",
      "Allenamento della voce attiva! Concentrarsi su chi compie l'azione rende le frasi più direte e facili da capire. Fai pratica con noi!",
      "Pronto per la voce attiva? Questo esercizio ti aiuterà a suonare più naturale e dinamico quando parli la nuova lingua. Segui gli esempi."
    ]
  },
  ru: {
    grammarWorkingOnIt: "Мы работаем над этим, контент будет добавлен в ближайшее время.",
    translatePage: "Перевести страницу",
    errorLoading: "Не удалось загрузить объяснение.",
    goldenTip: "Золотой совет",
    next: "Далее",
    startQuiz: "Начать тест",
    correctFeedback: "Правильно!",
    incorrectFeedback: "Неправильно!",
    continueBtn: "Продолжить",
    excellentResult: "Отлично! 🎉",
    keepPracticingResult: "Продолжайте практиковаться! 💪",
    correctAnswersCount: "Правильные ответы",
    backToTrack: "Назад к треку",
    scoreWarning: "Вам нужно набрать 7 баллов, чтобы завершить этот модуль.",
    back: "Назад",
    congratsTitle: "ПОЗДРАВЛЯЕМ! 🏆",
    congratsText: "Вы успешно выполнили все упражнения по Теме {num} и разблокировали следующий уровень своего плана обучения!",
    uauContinue: "Ого, Продолжить!",
    tryAgain: "Попробовать еще раз",
    minScoreWarning: "Вам нужно правильно ответить как минимум на 7 вопросов, чтобы завершить тему.",
    topicWelcomeTitle: "Добро пожаловать в Тему {num}",
    topicWelcomeIntro: "В этой теме вы будете практиковаться и закреплять свои знания!",
    topicWelcomeGrammarIntro: "В этой теме вы узнаете немного больше о грамматике и выполните упражнения!",
    topicContainsGrammar: "Грамматическое объяснение и дидактический урок",
    topicContainsQuiz: "10 тестов на закрепление (Викторина)",
    topicContainsPronunciation: "5 упражнений для проверки вашего произношения",
    topicContainsFlashcards: "5 карточек для закрепления языка",
    topicContainsWriting: "1 письменное упражнение для практики перевода",
    confirm: "Подтвердить",
    skip: "Пропустить",
    blockedBonusTitle: "Бонусный Тема Заблокирован!",
    blockedBonusMsg: "Завершите 10 предыдущих тем дорожной карты, чтобы разблокировать этот бонус.",
    blockedTopicTitle: "Тема Заблокирована!",
    blockedTopicMsg: "Завершите предыдущие темы, чтобы разблокировать.",
    errorExplanation: "Объяснение ошибки:",
    premiumLimitTitle: "Лимит достигнут",
    premiumLimitMessage: "Вы достигли суточного лимита исправлений. Перейдите на LibriTui Premium для неограниченного количества исправлений от Учителя!",
    close: "Закрыть",
    freeCorrectionsLabel: "Ежедневные бесплатные исправления: {count}/5",
    motivationalPhrases: [
      "Поздравляем! Тема почти завершена, вы отлично справляетесь!",
      "Отличный прогресс! Так держать, и вы овладеете языком!",
      "У вас отлично получается! Переходим к следующему этапу!",
      "Невероятно! Ваши усилия окупаются!",
      "Очень хорошо! Шаг за шагом вы придете к цели!",
      "Отличная работа! Сохраняйте фокус и продолжайте сиять!",
      "Сенсационно! Ваше обучение идет в гору!",
      "Идеально! Каждый завершенный этап приближает вас к свободной речи!",
      "Поздравляем с усердием! Вы быстро развиваетесь!",
      "Замечательно! Продолжаем в том же духе с отличной энергией!"
    ],
    flashcardIntros: [
      "Сейчас мы будем выполнять упражнения с карточками. Постарайтесь перевести или понять контекст предложения перед тем, как кликнуть.",
      "Добро пожаловать в карточки! Постарайтесь вспомнить значение каждого слова перед тем, как открыть ответ. Интервальное повторение — ключ к успеху!",
      "Готовы к карточкам? Посмотрите на слово или фразу, сделайте умственное усилие для перевода, а затем проверьте себя."
    ],
    pronunciationIntros: [
      "Время потренировать произношение! Говорить вслух необходимо для развития речи и уверенности. Внимательно слушайте аудио и повторяйте.",
      "Давайте потренируем устную речь! Правильное произношение приходит с внимательным прослушиванием и подражанием. Запишите свой голос.",
      "Следующий шаг: Произношение! Лучший способ побороть страх говорить — практиковаться с самого начала. Настройте микрофон и повторяйте!"
    ],
    grammarIntros: [
      "Сейчас мы изучим немного грамматики. Понимание структуры языка дает вам свободу уверенно строить собственные предложения. Внимательно читайте!",
      "Давайте погрузимся в грамматические правила! Грамматика — это скелет языка. Прочитайте практические примеры, а затем ответьте на вопросы.",
      "Время понять логику языка! Грамматика помогает нам последовательно организовывать слова. Обратите внимание на урок и пройдите тест."
    ],
    writingIntros: [
      "Цель этого упражнения — развить письменную речь. Перевод текстов помогает активно закреплять словарный запас и грамматику. Напишите ответ!",
      "Письменная практика! Письмо от руки или ввод текста на новом языке заставляют ваш мозг активно вспоминать изученные слова. Сделайте перевод!",
      "Давайте потренируем письмо! Это упражнение отлично подходит для оттачивания грамматической точности. Переведите предложенный текст!"
    ],
    activeVoiceIntros: [
      "Давайте попрактикуем активный залог! Преобразование предложений или прямое выражение действий делает ваше общение более ясным и естественным.",
      "Тренировка активного залога! Фокусировка на исполнителе действия делает ваши предложения более прямыми и понятными. Практикуйтесь!",
      "Готовы потренировать активный залог? Это упражнение поможет вам звучать более естественно и динамично в разговоре. Обратите внимание на примеры."
    ]
  },
  zh: {
    grammarWorkingOnIt: "我们正在努力，内容将很快添加。",
    translatePage: "翻译页面",
    errorLoading: "无法加载解析内容。",
    goldenTip: "黄金提示",
    next: "下一步",
    startQuiz: "开始测试",
    correctFeedback: "正确！",
    incorrectFeedback: "错误！",
    continueBtn: "继续",
    excellentResult: "太棒了！🎉",
    keepPracticingResult: "继续练习！💪",
    correctAnswersCount: "答对题数",
    backToTrack: "返回学习路径",
    scoreWarning: "您需要获得 7 分才能完成该模块。",
    back: "返回",
    congratsTitle: "恭喜！🏆",
    congratsText: "您成功完成了主题 {num} 的所有练习，并解锁了学习路径的下一个级别！",
    uauContinue: "哇，继续！🔥",
    tryAgain: "重试",
    minScoreWarning: "您需要答对至少 7 道题才能完成该主题。",
    topicWelcomeTitle: "欢迎来到主题 {num}",
    topicWelcomeIntro: "在这个主题中，你将练习并巩固你的学习！",
    topicWelcomeGrammarIntro: "在这个主题中，你将学习更多关于语法的内容并完成练习！",
    topicContainsGrammar: "语法解析与教学课程",
    topicContainsQuiz: "10 道巩固测试（Quiz）",
    topicContainsPronunciation: "5 个发音测试练习",
    topicContainsFlashcards: "5 张辅助语言记忆的闪卡（Flashcards）",
    topicContainsWriting: "1 个翻译写作练习",
    confirm: "确认",
    skip: "跳过",
    blockedBonusTitle: "奖励主题已锁定！",
    blockedBonusMsg: "完成路线图中的前 10 个主题以解锁此奖励。",
    blockedTopicTitle: "主题已锁定！",
    blockedTopicMsg: "完成前面的主题以解锁。",
    errorExplanation: "错误解析：",
    premiumLimitTitle: "已达上限",
    premiumLimitMessage: "您已达到每日批改上限。升级为 LibriTui Premium，即可享受老师无限次批改！",
    close: "关闭",
    freeCorrectionsLabel: "每日免费批改次数：{count}/5",
    motivationalPhrases: [
      "恭喜！主题即将完成，你做得很好！",
      "进步神速！继续保持，你将精通这门语言！",
      "你做得太棒了！让我们进入下一个阶段！",
      "令人惊叹！你的努力正在取得回报！",
      "非常好！一步一个脚印，你终会成功！",
      "做得好！保持专注，继续闪耀！",
      "太棒了！你的学习之旅正在起飞！",
      "完美！完成的每一个阶段都让你离流利更近一步！",
      "为你的努力点赞！你的进步非常快！",
      "太妙了！让我们带着这份满满的能量继续前进！"
    ],
    flashcardIntros: [
      "现在我们将进行闪卡练习。在点击查看翻译之前，请务必尝试翻译或理解句子的上下文。从长远来看，这有助于自然地记忆词汇。加油！",
      "欢迎使用闪卡！在显示答案之前，试着回忆每个单词的意思。间隔重复是把词汇固定在长期记忆中的最佳技巧之一！",
      "准备好使用闪卡了吗？看着单词或短语，在脑海中努力翻译，然后核对。这种努力正是强化学习神经连接的关键！"
    ],
    pronunciationIntros: [
      "是时候练习发音了！大声说出来对于锻炼发音肌肉和建立信心至关重要。仔细听音频，点击麦克风并重复句子。不要害怕犯错！",
      "现在让我们练习口语！正确的发音来自专注的倾听和模仿。听一听示例，录下你的声音并查看反馈。每一次尝试都让你离流利更近一步！",
      "下一步：发音！克服口语恐惧的最佳方法是从一开始就进行练习。调整好麦克风，深呼吸并重复你听到的句子。你可以的！"
    ],
    grammarIntros: [
      "现在我们将学习一些语法。理解语言结构能让你自信地构建自己的句子。仔细阅读教学解析，完成后我们将进行测试！",
      "让我们沉浸在语法规则中！语法是语言的骨架。理解句子的组成方式将大大加快你的学习速度。阅读实用示例并回答测试！",
      "是时候理解语言的逻辑了！语法帮助我们连贯地组织词汇。注意接下来的课程，并在之后通过问答来确保你完全理解。祝你好运！"
    ],
    writingIntros: [
      "本练习的目的是培养你的写作和句子结构能力。翻译文本有助于主动巩固词汇和语法。写下你的答案并接受 AI 老师的详细反馈！",
      "写作练习！在新语言中书写或打字会强迫你的大脑主动检索学过的词汇和规则。在翻译中尽最大努力，并查看改进建议！",
      "现在让我们练习写作！这个练习对于精炼你的语法精确度和词汇非常有用。翻译给定的文本，我们的 AI 老师将帮助你完善写作风格！"
    ],
    activeVoiceIntros: [
      "让我们练习主动语态！直接转换句子或表达动作有助于使你在日常交流中更加清晰、生动和自然。让我们开始吧！",
      "主动语态训练！关注谁执行了动作会让你的句子更加直接和易懂。练习主动语态构建并建立口语和写作的信心！",
      "准备好锻炼主动语态了吗？这个练习将帮助你在用新语言交流时听起来更自然、更生动。注意示例并多加练习！"
    ]
  },
  ja: {
    grammarWorkingOnIt: "現在作成中です。コンテンツはまもなく追加されます。",
    translatePage: "ページを翻訳",
    errorLoading: "解説を読み込めませんでした。",
    goldenTip: "特別ヒント",
    next: "次へ",
    startQuiz: "クイズを開始",
    correctFeedback: "正解！",
    incorrectFeedback: "不正解！",
    continueBtn: "続行",
    excellentResult: "素晴らしい！🎉",
    keepPracticingResult: "練習を続けましょう！💪",
    correctAnswersCount: "正解数",
    backToTrack: "コースに戻る",
    scoreWarning: "このモジュールを完了するには7点以上が必要です。",
    back: "戻る",
    congratsTitle: "おめでとうございます！🏆",
    congratsText: "トピック {num} のすべてのエクササイズを正常に完了し、ロードマップの次のレベルのロックを解除しました！",
    uauContinue: "うわー、続ける！🔥",
    tryAgain: "もう一度試す",
    minScoreWarning: "トピックを完了するには、少なくとも7問に正解する必要があります。",
    topicWelcomeTitle: "トピック {num} へようこそ",
    topicWelcomeIntro: "このトピックでは、学習した内容を練習して定着させます！",
    topicWelcomeGrammarIntro: "このトピックでは、文法について少し詳しく学び、エクササイズを完了します！",
    topicContainsGrammar: "文法の解説と学習レッスン",
    topicContainsQuiz: "10問の定着クイズ（Quiz）",
    topicContainsPronunciation: "発音をテストする5つのエクササイズ",
    topicContainsFlashcards: "言語の定着を助ける5枚のフラッシュカード",
    topicContainsWriting: "翻訳を練習する1つのライティングエクササイズ",
    confirm: "確認する",
    skip: "スキップ",
    blockedBonusTitle: "ボーナストピックはロックされています！",
    blockedBonusMsg: "このボーナスを解除するには、ロードマップの前の10個のトピックを完了してください。",
    blockedTopicTitle: "トピックはロックされています！",
    blockedTopicMsg: "ロックを解除するには、前のトピックを完了してください。",
    errorExplanation: "誤りの解説：",
    premiumLimitTitle: "上限に達しました",
    premiumLimitMessage: "今日の添削制限に達しました。先生による無制限の添削を受けるには、LibriTui Premiumにアップグレードしてください！",
    close: "閉じる",
    freeCorrectionsLabel: "今日の無料添削：{count}/5",
    motivationalPhrases: [
      "おめでとうございます！トピックはほぼ完了しました。順調です！",
      "素晴らしい進歩です！その調子で言語をマスターしましょう！",
      "とてもよくできています！次のステップに進みましょう！",
      "素晴らしい！努力が実を結んでいます！",
      "とても良いです！一歩一歩進めば目標に到達できます！",
      "よくできました！集中力を維持して、輝き続けましょう！",
      "素晴らしい！あなたの学習は軌道に乗っています！",
      "完璧です！完了した各ステージが流暢さに近づけてくれます！",
      "努力にお祝い申し上げます！急速に進化しています！",
      "素晴らしい！この素晴らしいエネルギーで続けましょう！"
    ],
    flashcardIntros: [
      "これからフラッシュカードのエクササイズを行います。翻訳を見る前に、文脈を自分で訳すか理解するように努めることが大切です。頑張りましょう！",
      "フラッシュカードへようこそ！答えを表示する前に、各単語の意味を思い出してみてください。間隔反復は長期記憶に定着させる最良のテクニックです！",
      "フラッシュカードの準備はいいですか？単語やフレーズを見て、頭の中で翻訳する努力をしてから確認しましょう。その努力が脳を鍛えます！"
    ],
    pronunciationIntros: [
      "発音を練習する時間です！声に出して話すことは、発音の筋肉を鍛え、自信をつけるために不可欠です。音声をよく聞き、マイクを押して繰り返しましょう。",
      "さあ、スピーキングの練習をしましょう！正しい発音は、注意深いリスニングと模倣から生まれます。例を聞き、声を録音してフィードバックを確認しましょう。",
      "次のステップ：発音！話す恐怖を克服する最善の方法は、最初から練習することです。マイクを調整し、深呼吸して聞こえたフレーズを繰り返します。"
    ],
    grammarIntros: [
      "これから文法を少し学びます。言語の構造を理解することで、自信を持って自分の文章を作ることができるようになります。解説をよく読んでください！",
      "文法規則に飛び込みましょう！文法は言語の骨組みです。文がどのように構成されているかを理解することは、学習を大幅にスピードアップさせます。",
      "言語の論理を理解する時間です！文法は単語を一貫して整理するのに役立ちます。次のレッスンに注意を払い、その後のクイズで理解を確認しましょう。"
    ],
    writingIntros: [
      "このエクササイズの目的は、ライティング能力を養うことです。テキストを翻訳することは、語彙と文法を能動的に定着させるのに役здаる。書いてみましょう！",
      "ライティング練習！新しい言語で手書きしたりタイピングしたりすることは、脳に学んだ単語や規則を能動的に思い出させます。翻訳を精一杯行いましょう！",
      "さあ、書く練習をしましょう！このエクササイズは、文法の正確さと語彙を磨くのに最適です。提示されたテキストを翻訳してみましょう！"
    ],
    activeVoiceIntros: [
      "能動態を練習しましょう！文を変換したり動作を直接表現したりすることで、日常のコミュニケーションがより明確で自然になります。始めましょう！",
      "能動態トレーニング！誰が動作を行うかに焦点を当てることで、文がより直接的で理解しやすくなります。能動的な構成を練習して自信をつけましょう！",
      "能動態を練習する準備はできましたか？このエクササイズは、新しい言語で話す際により自然でダイナミックに聞こえるようにします。例に注意しましょう。"
    ]
  },
  nl: {
    grammarWorkingOnIt: "We werken hieraan, de inhoud wordt binnenkort toegevoegd.",
    translatePage: "Vertaal pagina",
    errorLoading: "Kan de uitleg niet laden.",
    goldenTip: "Gouden tip",
    next: "Volgende",
    startQuiz: "Start Quiz",
    correctFeedback: "Correct!",
    incorrectFeedback: "Onjuist!",
    continueBtn: "Doorgaan",
    excellentResult: "Uitstekend! 🎉",
    keepPracticingResult: "Blijf oefenen! 💪",
    correctAnswersCount: "Correcte antwoorden",
    backToTrack: "Terug naar het pad",
    scoreWarning: "Je moet 7 scoren om deze module te voltooien.",
    back: "Terug",
    congratsTitle: "GEFELICITEERD! 🏆",
    congratsText: "Je hebt met succes alle oefeningen voor Onderwerp {num} voltooid en het volgende niveau van je roadmap ontgrendeld!",
    uauContinue: "Wauw, doorgaan!",
    tryAgain: "Opnieuw proberen",
    minScoreWarning: "Je moet minimaal 7 vragen correct beantwoorden om het onderwerp te voltooien.",
    topicWelcomeTitle: "Welkom bij Onderwerp {num}",
    topicWelcomeIntro: "In dit onderwerp ga je oefenen en je leerstof consolideren!",
    topicWelcomeGrammarIntro: "In dit onderwerp leer je wat meer over grammatica en voltooi je oefeningen!",
    topicContainsGrammar: "Grammatica-uitleg en didactische les",
    topicContainsQuiz: "10 consolidatietesten (Quiz)",
    topicContainsPronunciation: "5 oefeningen om je uitspraak te testen",
    topicContainsFlashcards: "5 flashcards om de taal te helpen onthouden",
    topicContainsWriting: "1 schrijfoefening om vertaling te oefenen",
    confirm: "Bevestigen",
    skip: "Overslaan",
    blockedBonusTitle: "Bonusonderwerp Vergrendeld!",
    blockedBonusMsg: "Voltooi de 10 vorige onderwerpen van de roadmap om deze bonus te ontgrendelen.",
    blockedTopicTitle: "Onderwerp Vergrendeld!",
    blockedTopicMsg: "Voltooi de vorige onderwerpen om te ontgrendelen.",
    errorExplanation: "Uitleg van de fout:",
    premiumLimitTitle: "Limiet bereikt",
    premiumLimitMessage: "Je hebt je dagelijkse correctielimiet bereikt. Upgrade naar LibriTui Premium voor onbeperkte correcties door de docent!",
    close: "Sluiten",
    freeCorrectionsLabel: "Dagelijkse gratis correcties: {count}/5",
    motivationalPhrases: [
      "Gefeliciteerd! Onderwerp bijna voltooid, je bent goed bezig!",
      "Uitstekende vooruitgang! Ga zo door en je zult de taal beheersen!",
      "Je doet het geweldig! Laten we naar de volgende fase gaan!",
      "Geweldig! Je inspanningen werpen hun vruchten af!",
      "Zeer goed! Stap voor stap kom je er wel!",
      "Goed werk! Blijf gefocust en blijf blinken!",
      "Sensationeel! Je leerproces neemt een vlucht!",
      "Perfect! Elke voltooide fase brengt je dichter bij vloeiendheid!",
      "Gefeliciteerd met je inzet! Je ontwikkelt je snel!",
      "Prachtig! Laten we doorgaan met deze geweldige energie!"
    ],
    flashcardIntros: [
      "Nu gaan we flashcard-oefeningen doen. Het is belangrijk dat je probeert te vertalen of de context te begrijpen voordat je klikt om de vertaling te zien. Succes!",
      "Welkom bij de flashcards! Probraz de betekenis van elk woord te onthouden voordat je het antwoord onthult. Gespreide herhaling is een van de beste technieken!",
      "Klaar voor flashcards? Kijk naar het woord of de zin, doe een mentale inspanning om te vertalen en controleer dan. Dit versterkt de neurale verbindingen!"
    ],
    pronunciationIntros: [
      "Tijd om je uitspraak te oefenen! Hardop spreken is essentieel om je spreekspieren te ontwikkelen en zelfvertrouwen te winnen. Luister aandachtig en herhaal de zin.",
      "Laten we nu het spreken oefenen! De juiste uitspraak komt door aandachtig luisteren en imiteren. Luister naar het voorbeeld, neem je stem op en bekijk de feedback.",
      "Volgende stap: Uitspraak! De beste manier om angst om te spreken te overwinnen, is door vanaf het begin te oefenen. Pas de microfono aan en herhaal!"
    ],
    grammarIntros: [
      "Nu gaan we wat grammatica leren. Het begrijpen van de taalstructuur geeft je de vrijheid om zelfverzekerd je eigen zinnen te bouwen. Lees de uitleg aandachtig.",
      "Laten we in de grammaticaregels duiken! Grammatica is het skelet van de taal. Begrijpen hoe zinnen worden gevormd, zal je leerproces aanzienlijk versnellen.",
      "Tijd om de logica van de taal te begrijpen! Grammatica helpt ons om woorden coherent te ordenen. Let goed op de volgende les en voltooi daarna de quiz."
    ],
    writingIntros: [
      "Het doel van deze oefening is om je schrijfvaardigheid te ontwikkelen. Het vertalen van teksten helpt om woordenschat en grammatica actief te consolideren.",
      "Schrijfoefening! Schrijven of typen in de nieuwe taal dwingt je hersenen om geleerde woorden en regels actief op te roepen. Doe je best bij de vertaling!",
      "Laten we nu het schrijven oefenen! Deze oefening is uitstekend voor het verfijnen van je grammaticale precisie en woordenschat. Vertaal de voorgestelde tekst."
    ],
    activeVoiceIntros: [
      "Laten we de actieve vorm oefenen! Het omzetten van zinnen of het direct uitdrukken van acties helpt om je communicatie in het dagelijks leven duidelijker te maken.",
      "Actieve vorm training! Focussen op wie de actie uitvoert, maakt je zinnen directer en begrijpelijker. Oefen met actieve structurering en win aan vertrouwen!",
      "Klaar om de actieve vorm te oefenen? Deze oefening helpt je om natuurlijker en dynamischer te klinken wanneer je de nieuwe taal spreekt. Let op de voorbeelden."
    ]
  }
};

const SUCCESS_SOUND_URL = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/pronuncia-cards/assets/success.ogg';

const LANGUAGE_FLAGS: Record<string, string> = {
  'en': '🇺🇸',
  'fr': '🇫🇷',
  'de': '🇩🇪',
  'es': '🇪🇸',
  'it': '🇮🇹',
  'hu': '🇭🇺',
  'ru': '🇷🇺',
  'pt': '🇧🇷'
};

const LANGUAGE_NAMES: Record<string, string> = {
  'en': 'Inglês',
  'fr': 'Francês',
  'de': 'Alemão',
  'es': 'Espanhol',
  'it': 'Italiano',
  'hu': 'Húngaro',
  'ru': 'Russo',
  'pt': 'Português'
};

// Simple Levenshtein distance implementation
const getLevenshteinDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

const getSimilarity = (s1: string, s2: string): number => {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - getLevenshteinDistance(longer, shorter)) / parseFloat(longerLength.toString());
};

const normalizeForComparison = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[.,\/#!$%\^&\*;:{}=\-_~()?]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const GRAMMAR_LABELS: Record<string, Record<string, string[]>> = {
  en: {
    a1: [
      'Verbo To Be', 'Pronomes Sujeitos', 'Artigos', 'Substantivos no Plural',
      'Pronomes Demonstrativos', 'Adjetivos Possessivos', 'Presente Simples',
      'Advérbios de Frequência', 'Preposições de Tempo', 'Preposições de Lugar',
      'Habilidades (Can/Can\'t)', 'Pronomes Objeto', 'Existência (There is/are)',
      'Passado Simples (To Be)', 'Imperativos'
    ],
    a2: [
      'Presente Contínuo', 'Passado Simples', 'Comparativos e Superlativos',
      'Futuro (Will / Going to)', 'Modais de Obrigação', 'Presente Perfeito',
      'Contáveis e Incontáveis', 'Advérbios de Modo', 'Preposições de Movimento',
      'Conjunções', 'Voz Passiva', 'Orações Relativas',
      'Primeiro Condicional', 'Used To', 'Discurso Indireto'
    ],
    b1: [
      'Passado Contínuo', 'Presente Perfeito Contínuo', 'Passado Perfeito',
      'Segundo Condicional', 'Verbos Modais', 'Gerúndio e Infinitivo',
      'Used To (Costumava)', 'Tag Questions', 'Orações Relativas', 'Passiva com Modais'
    ],
    b2: [
      'Terceiro Condicional', 'Condicionais Mistas', 'Passiva com Verbos de Relato',
      'Forma Causativa', 'Futuro Contínuo e Perfeito', 'Artigos Avançados',
      'Inversão para Ênfase', 'Palavras de Conexão', 'Wish & If Only', 'Quantificadores Avançados'
    ],
    c1: [
      'Modo Subjuntivo', 'Orações Participiais', 'Cleft Sentences',
      'Inversão Condicional Avançada', 'Nominalização', 'Elipse e Substituição',
      'Adverbiais Avançados', 'Adjetivos Compostos', 'Stance Adverbials', 'Ênfase e Foco'
    ],
    c2: [
      'Hipóteses de Passado Distante', 'Conectores Sofisticados', 'Duplos Comparativos',
      'Passiva Modal Complexa', 'Phrasal Verbs Idiomáticos', 'Inversão Estilística',
      'Discurso Indireto Avançado', 'Figuras Retóricas', 'Coesão Textual', 'Controle de Registro'
    ]
  },
  fr: {
    a1: [
      'Verbo Être', 'Verbo Avoir', 'Pronomes Sujeitos', 'Artigos Definidos',
      'Artigos Indefinidos', 'Gênero e Número', 'Verbos em -ER no Presente',
      'Verbos em -IR e -RE', 'Negação (Ne... Pas)', 'Perguntas Simples',
      'Adjetivos Qualificativos', 'Adjetivos Possessivos', 'Preposições de Lugar',
      'Horas e Datas', 'Il y a e C\'est'
    ],
    a2: [
      'Passé Composé', 'Imparfait', 'Futur Proche', 'Futur Simple',
      'Pronomes Objeto Direto', 'Pronomes Objeto Indireto', 'Pronomes Y e En',
      'Comparativo e Superlativo', 'Advérbios', 'Verbos Pronominais',
      'Expressão de Quantidade', 'Pronomes Relativos', 'Obrigação e Proibição',
      'Condição com Si', 'Expressões de Tempo'
    ],
    b1: [
      'Plus-que-parfait', 'Condicional Presente', 'Discurso Indireto',
      'Introdução ao Subjuntivo', 'Voz Passiva', 'Gerúndio',
      'Conectores Lógicos', 'Expressão de Sentimentos', 'Hypothèse',
      'Pronomes Relativos Avançados'
    ],
    b2: [
      'Subjuntivo Avançado', 'Condicional Passado', 'Discurso Relatado',
      'Nominalização', 'Expressão de Causa', 'Expressão de Consequência', 'Expressão de Fim',
      'Expressão de Oposição', 'Conectores Avançados', 'Registros de Língua'
    ],
    c1: [
      'Subjuntivo Complexo', 'Estilo Formal', 'Construções Impessoais',
      'Discurso Formal', 'Modalidade', 'Figuras de Estilo',
      'Conectores Acadêmicos', 'Abstração', 'Sintaxe Complexa',
      'Linguagem das Mídias'
    ],
    c2: [
      'Registro Literário', 'Nuances Semânticas', 'Retórica',
      'Variações Estilísticas', 'Expressions Idiomatiques', 'Argumentação',
      'Implicite', 'Linguagem Acadêmica', 'Mestrado do Discurso',
      'Registro e Nuance'
    ]
  },
  it: {
    a1: [
      "Che cos'è il verbo 'Essere'?",
      'Articoli Determinativi e Indeterminativi',
      'Sostantivi Regolari',
      'Aggettivi Qualificativi',
      'Verbi Regolari in -are',
      'Verbi Regolari in -ere',
      'Verbi Regolari in -ire',
      'Verbi Ausiliari (Essere / Avere)',
      'Articoli (Uso e Revisione)',
      'Pronomi Personali Soggetto',
      'Preposizioni Semplici',
      'Preposizioni Articolate',
      'Verbi Riflessivi',
      'Il Passato Prossimo',
      'Verbi Modali (Potere, Volere, Dovere)'
    ],
    a2: [
      "L'imperfetto Indicativo",
      'Gli Aggettivi e Pronomi Possessivi',
      'Il Futuro Semplice',
      'Il Futuro Anteriore',
      'I Pronomi Diretti',
      'I Pronomi Indiretti',
      'Passato Prossimo vs Imperfetto',
      'I Pronomi Ci e Ne',
      'Gli Avverbi di Modo e Tempo',
      'I Pronomi Combinati',
      'I Pronomi Relativi (Che, Cui)',
      'I Verbi Pronominali',
      "Espressione dell'Obbligo e Divieto",
      'La Condizione con Se',
      "L'Espressione del Tempo"
    ],
    b1: [
      'Il Passato Remoto',
      'Il Trapassato Prossimo',
      'Il Condizionale Presente e Passato',
      'Introduzione al Congiuntivo',
      'Il Congiuntivo Presente',
      'Il Congiuntivo Passato',
      'Il Congiuntivo Imperfetto',
      'La Forma Passiva e Gerundio',
      'Il Periodo Ipotetico',
      'Il Discorso Indiretto'
    ],
    b2: [
      'Il Congiuntivo Trapassato e Avanzato',
      'Il Si Impersonale e Passivante',
      'Il Discorso Riportato Avanzato',
      'La Nominalizzazione',
      'Esprimere la Causa',
      'Esprimere la Conseguenza',
      'Esprimere lo Scopo',
      "Esprimere l'Opposizione e Concessione",
      'I Connettori Testuali Avanzati',
      'I Registri Linguistici'
    ],
    c1: [
      'Il Congiuntivo nei Periodi Complessi',
      'Lo Stile Sostenuto e Formale',
      'Le Costruzioni Impersonali Avanzate',
      'Il Discorso Formale ed Epistolare',
      'La Modalità ed Espressione del Dubbio',
      'Le Figure Retoriche e di Stile',
      'I Connettori Accademici',
      "L'Astrazione e Linguaggio Specialistico",
      'La Sintassi Complessa',
      'La Lingua dei Media e Giornalismo'
    ],
    c2: [
      'Il Registro Letterario e Arcaico',
      'Le Sfumature Semantiche e Sinonimia',
      'La Retorica e Persuasione Avanzata',
      "Le Variazioni Stilistiche d'Autore",
      'Le Espressioni Idiomatiche Complesse',
      "L'Argomentazione Filosofica e Giuridica",
      "L'Implicito e l'Ironia nel Discorso",
      'La Lingua Accademica Superiore',
      'La Padronanza Assoluta del Discorso',
      'Registro, Nuance e Sensibilità Linguistica'
    ]
  }
};

// Idiomas novos (es, pt, de, ru, hu): rótulos em português vêm de constants/grammarLanguages.ts
for (const [code, meta] of Object.entries(NEW_GRAMMAR_LANGUAGES)) {
  GRAMMAR_LABELS[code] = Object.fromEntries(Object.entries(meta.levels).map(([lv, mods]) => [lv, mods.map(m => m.labelPt)]));
}

const GRAMMAR_MODULE_SUFFIXES: Record<string, Record<string, string[]>> = {
  en: {
    a1: [
      'verb_to_be', 'subject_pronouns', 'articles', 'plural_nouns',
      'demonstrative_pronouns', 'possessive_adjectives', 'present_simple',
      'adverbs_of_frequency', 'prepositions_of_time', 'prepositions_of_place',
      'can_cant', 'object_pronouns', 'there_is_there_are',
      'past_simple_verb_to_be', 'imperatives'
    ],
    a2: [
      'present_continuous', 'past_simple', 'comparatives_superlatives',
      'future_will_going_to', 'modals_obligation', 'present_perfect',
      'countable_uncountable', 'adverbs_of_manner', 'prepositions_movement',
      'conjunctions', 'passive_voice', 'relative_clauses',
      'first_conditional', 'used_to', 'reported_speech'
    ],
    b1: [
      'past_continuous', 'present_perfect_continuous', 'past_perfect',
      'second_conditional', 'modal_verbs', 'gerund_infinitive',
      'used_to', 'tag_questions', 'relative_clauses', 'modal_passive'
    ],
    b2: [
      'third_conditional', 'mixed_conditionals', 'passive_reporting_verbs',
      'causative_form', 'future_continuous_perfect', 'advanced_articles',
      'inversion_for_emphasis', 'linking_words', 'wish_if_only', 'advanced_quantifiers'
    ],
    c1: [
      'subjunctive_mood', 'participle_clauses', 'cleft_sentences',
      'advanced_conditional_inversion', 'nominalization', 'ellipsis_substitution',
      'advanced_adverbials', 'compound_adjectives', 'stance_adverbials', 'emphasis_focus'
    ],
    c2: [
      'distant_past_hypotheses', 'sophisticated_connectors', 'double_comparatives',
      'complex_modal_passive', 'idiomatic_phrasal_verbs', 'stylistic_inversion',
      'advanced_reported_speech', 'rhetorical_figures', 'textual_cohesion', 'register_control'
    ]
  },
  fr: {
    a1: [
      'verbe_etre', 'verbe_avoir', 'pronoms_sujets', 'articles_definis',
      'articles_indefinis', 'genre_et_nombre', 'present_des_verbes_er',
      'verbes_ir_et_re', 'negation_ne_pas', 'questions_simples',
      'adjectifs_qualificatifs', 'adjectifs_possessifs', 'prepositions_de_lieu',
      'heure_et_date', 'il_y_a_et_c_est'
    ],
    a2: [
      'passe_compose', 'imparfait', 'futur_proche', 'futur_simple',
      'pronoms_objets_directs', 'pronoms_objets_indirects', 'pronoms_y_et_en',
      'comparatif_et_superlatif', 'adverbes', 'verbes_pronominaux',
      'expression_de_la_quantite', 'pronoms_relatifs', 'obligation_et_interdiction',
      'condition_si', 'expression_du_temps'
    ],
    b1: [
      'plus_que_parfait', 'conditionnel_present', 'discours_indirect',
      'subjonctif_introduction', 'voix_passive', 'gerondif',
      'connecteurs_logiques', 'expression_des_sentiments', 'hypothese',
      'pronoms_relatifs_avances'
    ],
    b2: [
      'subjonctif_avance', 'conditionnel_passe', 'discours_rapporte',
      'nominalisation', 'cause', 'consequence', 'but',
      'opposition', 'connecteurs_avances', 'registres_de_langue'
    ],
    c1: [
      'subjonctif_complexe', 'style_soutenu', 'constructions_impersonnelles',
      'discours_formel', 'modalite', 'figures_de_style',
      'connecteurs_academiques', 'abstraction', 'syntaxe_complexe',
      'langue_des_medias'
    ],
    c2: [
      'registre_litteraire', 'nuances_semantiques', 'rhetorique',
      'variations_stylistiques', 'expressions_idiomatiques', 'argumentation',
      'implicite', 'langue_academique', 'maitrise_du_discours',
      'registre_et_nuance'
    ]
  },
  it: {
    a1: [
      'verbo_essere',
      'articoli_determinativi_e_indeterminativi',
      'sostantivi_regolari',
      'aggettivi_qualificativi',
      'i_verbi_regolari_in_are',
      'i_verbi_regolari_in_ere',
      'i_verbi_regolari_in_ire',
      'i_verbi_ausiliari_essere_avere',
      'articoli_determinativi_indeterminativi',
      'i_pronome_personale_soggetto',
      'le_preposizioni_semplici',
      'le_preposizioni_articolate',
      'i_verbi_riflessivi',
      'il_passato_prossimo',
      'i_verbi_modali_potere_volere_dovere'
    ],
    a2: [
      'passe_compose',
      'gli_aggettivi_possessivi',
      'futur_proche',
      'futur_simple',
      'i_pronomi_diretti',
      'i_pronomi_indiretti',
      'passato_prossimo_vs_imperfetto',
      'i_verbi_riflessivi',
      'adverbes',
      'i_pronomi_combinati',
      'i_pronomi_relativi',
      'pronoms_relatifs',
      'obligation_et_interdiction',
      'condition_si',
      'expression_du_temps'
    ],
    b1: [
      'plus_que_parfait',
      'il_trapassato_prossimo',
      'discours_indirect',
      'subjonctif_introduction',
      'il_congiuntivo_presente',
      'il_congiuntivo_passato',
      'il_congiuntivo_imperfetto',
      'expression_des_sentiments',
      'il_periodo_ipotetico',
      'il_discorso_indiretto'
    ],
    b2: [
      'subjonctif_avance',
      'il_si_impersonale_e_passivante',
      'discours_rapporte',
      'nominalisation',
      'cause',
      'consequence',
      'but',
      'opposition',
      'connecteurs_avances',
      'registres_de_langue'
    ],
    c1: [
      'subjonctif_complexe',
      'style_soutenu',
      'constructions_impersonnelles',
      'discours_formel',
      'modalite',
      'figures_de_style',
      'connecteurs_academiques',
      'abstraction',
      'syntaxe_complexe',
      'langue_des_medias'
    ],
    c2: [
      'registre_litteraire',
      'nuances_semantiques',
      'rhetorique',
      'variations_stylistiques',
      'expressions_idiomatiques',
      'argumentation',
      'implicite',
      'langue_academique',
      'maitrise_du_discours',
      'registre_et_nuance'
    ]
  }
};

// Sufixos dos idiomas novos: id "es_a1_01_verbos_ser_estar" -> "verbos_ser_estar"
for (const [code, meta] of Object.entries(NEW_GRAMMAR_LANGUAGES)) {
  GRAMMAR_MODULE_SUFFIXES[code] = Object.fromEntries(Object.entries(meta.levels).map(([lv, mods]) => [lv, mods.map(m => m.id.split('_').slice(3).join('_'))]));
}

const TRANSLATING_LABELS: Record<string, string> = {
  pt: 'Traduzindo...',
  en: 'Translating...',
  es: 'Traduciendo...',
  fr: 'Traduction...',
  de: 'Wird übersetzt...',
  it: 'Traducendo...',
  ru: 'Перевод...',
  zh: '正在翻译...',
  ja: '翻訳中...',
  nl: 'Vertalen...'
};

export default function RoadmapPage() {
  const { langCode, level } = useParams<{ langCode?: string; level?: string }>();
  const navigate = useNavigate();
  const { targetLanguage } = useTheme();
  // Idioma nativo do usuário: não aparece como opção de roadmap
  const nativeLangCode = (targetLanguage?.split('-')[0] || 'pt').toLowerCase();
  const { user } = useAuth();
  const { profile, refreshProfile } = useData();
  const { t, i18n } = useTranslation();

  // Active learning streak timer for the roadmap page (at least 3 minutes count as streak)
  useStreakCounter(true);

  const currentNativeLang = useMemo(() => {
    const lang = i18n.language || 'pt';
    return lang.substring(0, 2).toLowerCase();
  }, [i18n.language]);

  const localT = useMemo(() => {
    return LOCAL_TRANSLATIONS[currentNativeLang] || LOCAL_TRANSLATIONS.pt;
  }, [currentNativeLang]);

  const translatingLabel = useMemo(() => {
    return TRANSLATING_LABELS[(i18n.language || 'pt').split('-')[0].toLowerCase()] || TRANSLATING_LABELS.pt;
  }, [i18n.language]);
  
  const [viewState, setViewState] = useState<'roadmap' | 'practice'>('roadmap');
  const [practiceStage, setPracticeStage] = useState<'interleaved' | 'grammar' | 'writing'>('interleaved');
  const [activeTooltipNodeId, setActiveTooltipNodeId] = useState<string | null>(null);
  
  // Exercises practice state
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState<any[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [selectedWordInfo, setSelectedWordInfo] = useState<{
    word: string;
    translation: string;
    loading: boolean;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    const handleGlobalClick = () => {
      setSelectedWordInfo(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  // Active study tracker initialization & unmount flush for /roadmap
  useEffect(() => {
    const rawLang = langCode || profile?.learning_language;
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, langCode, profile?.learning_language]);

  const [isDeckSwapped, setIsDeckSwapped] = useState<boolean>(() => {
    try {
      return localStorage.getItem('libritui_flashcard_swap_mode') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSwapDeck = () => {
    setIsDeckSwapped(prev => {
      const next = !prev;
      try {
        localStorage.setItem('libritui_flashcard_swap_mode', String(next));
      } catch (e) {}
      return next;
    });
  };

  const unifiedDict = useUnifiedDictionary();

  const handleWordClick = async (e: React.MouseEvent<HTMLSpanElement>, word: string, isSwapped: boolean = false) => {
    e.stopPropagation();
    const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    const userUiLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
    const cardDeckLang = (langCode || 'en').split('-')[0].toLowerCase();
    
    const sourceLangCode = isSwapped ? userUiLang : cardDeckLang;
    const targetLangCode = isSwapped ? cardDeckLang : userUiLang;

    unifiedDict.handleWordClick(e, cleanWord, sourceLangCode, targetLangCode);
  };

  const renderClickableText = (text: string, isSwapped: boolean = false) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span 
            key={idx}
            onClick={(e) => handleWordClick(e, part, isSwapped)}
            className="hover:text-primary hover:underline cursor-pointer transition-colors relative inline-block text-2xl font-medium dark:text-ink"
          >
            {part}
          </span>
        );
      }
      return <span key={idx} className="text-2xl font-medium text-ink dark:text-slate-200">{part}</span>;
    });
  };

  const renderClickableTextForQuiz = (text: string) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span 
            key={idx}
            onClick={(e) => handleWordClick(e, part)}
            className="hover:text-primary hover:underline cursor-pointer transition-colors relative inline-block text-lg font-semibold dark:text-ink"
          >
            {part}
          </span>
        );
      }
      return <span key={idx} className="text-lg font-semibold text-ink dark:text-slate-200">{part}</span>;
    });
  };

  const [currentTopicId, setCurrentTopicId] = useState<string | null>(null);
  const [ttsRate, setTtsRate] = useState(0.9);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  
  // Speech Recognition state
  const [isListening, setIsListening] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [lastTranscript, setLastTranscript] = useState('');
  const [recognitionFeedback, setRecognitionFeedback] = useState<'success' | 'error' | null>(null);
  const recognitionRef = useRef<any>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Selected/Created roadmaps list states
  const [createdRoadmaps, setCreatedRoadmaps] = useState<any[]>([]);
  const [loadingCreated, setLoadingCreated] = useState(false);
  const [showAddLangSelector, setShowAddLangSelector] = useState(false);
  
  // Completion state loaded from local storage
  const [completedTopics, setCompletedTopics] = useState<string[]>([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showLevelMenu, setShowLevelMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [selectedNewLangModal, setSelectedNewLangModal] = useState<string | null>(null);

  // Cloudflare D1 progress state
  const [dbProgress, setDbProgress] = useState<{
    user_id: string;
    language_code: string;
    current_level: string;
    current_topic_index: number;
    last_updated?: string;
  } | null>(null);

  const CEFR_LEVELS = useMemo(() => ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'], []);

  // Grammar & Quiz practice state for Topic 3
  const [grammarView, setGrammarView] = useState<'explanation' | 'quiz' | 'result'>('explanation');
  const [studyModeChoice, setStudyModeChoice] = useState<'select' | 'text' | 'video'>('select');
  const [isGrammarUnavailable, setIsGrammarUnavailable] = useState(false);
  const [explanationSteps, setExplanationSteps] = useState<any[]>([]);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [isPageTranslated, setIsPageTranslated] = useState(false);
  const [translatedSteps, setTranslatedSteps] = useState<Record<number, any>>({});
  const [isTranslatingPage, setIsTranslatingPage] = useState(false);
  const [explanationFetchError, setExplanationFetchError] = useState(false);
  const [showTranslationErrorModal, setShowTranslationErrorModal] = useState(false);

  // Quiz Translation States
  const [isQuizTranslated, setIsQuizTranslated] = useState(false);
  const [translatedQuizPrompts, setTranslatedQuizPrompts] = useState<Record<number, string>>({});
  const [translatedQuizExplanations, setTranslatedQuizExplanations] = useState<Record<number, string>>({});
  const [isTranslatingQuiz, setIsTranslatingQuiz] = useState(false);

  // Welcome and Intro overlay states
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [activeTransitionPhrase, setActiveTransitionPhrase] = useState('');
  const [onConfirmTransition, setOnConfirmTransition] = useState<(() => void) | null>(null);
  const [currentExerciseIntroType, setCurrentExerciseIntroType] = useState<'flashcard' | 'pronunciation' | 'grammar' | 'writing' | 'activeVoice' | null>(null);
  const [activeIntroIndex, setActiveIntroIndex] = useState(0);

  // Free Tier writing limit validation states
  const [roadmapWritingCount, setRoadmapWritingCount] = useState(0);
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Bonus Topics State & PRO Access Control
  const [selectedBonusDef, setSelectedBonusDef] = useState<BonusTopicDef | null>(null);
  const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
  const [showProNoticeModal, setShowProNoticeModal] = useState(false);
  const [proModalMessage, setProModalMessage] = useState('');
  const [showLevelCompletionModal, setShowLevelCompletionModal] = useState(false);
  const [showBlockedTopicModal, setShowBlockedTopicModal] = useState(false);
  const [blockedTopicModalContent, setBlockedTopicModalContent] = useState<{ title: string; message: string }>({ title: '', message: '' });

  const handleBonusTopicClick = (bonusDef: BonusTopicDef) => {
    // 1. Check lock condition: preceding topic MUST be completed
    const precedingTopicId = `topic_${bonusDef.afterTopicIndex + 1}`; // 'topic_10', 'topic_20', 'topic_30'
    const precedingCompleted = isTopicCompleted(precedingTopicId);

    if (!precedingCompleted) {
      setBlockedTopicModalContent({
        title: localT.blockedBonusTitle || "Tópico Bônus Bloqueado!",
        message: localT.blockedBonusMsg || "Complete os 10 tópicos anteriores do roadmap para desbloquear este bônus."
      });
      setShowBlockedTopicModal(true);
      return;
    }

    // 2. Check PRO plan status
    const isPro = profile?.plan_type === 'pro' || profile?.subscription_tier === 'pro';
    if (!isPro) {
      setProModalMessage(
        PRO_REQUIRED_MESSAGES[currentNativeLang]?.message || PRO_REQUIRED_MESSAGES['pt'].message
      );
      setShowProNoticeModal(true);
    } else {
      setSelectedBonusDef(bonusDef);
      setIsBonusModalOpen(true);
    }
  };

  const handleContinueStudies = () => {
    setShowLevelCompletionModal(false);
    const currentLvlIdx = CEFR_LEVELS.indexOf((level || 'A1').toUpperCase());
    if (currentLvlIdx < CEFR_LEVELS.length - 1) {
      const nextLvl = CEFR_LEVELS[currentLvlIdx + 1].toLowerCase();
      navigate(`/roadmap/${langCode}/${nextLvl}`);
    }
  };

  // Single click lock for completing topic
  const [isCompletingTopic, setIsCompletingTopic] = useState(false);

  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalWrong, setTotalWrong] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [quizSessionId, setQuizSessionId] = useState('');

  // Writing practice state for Topics 4 and 5
  const [writingStory, setWritingStory] = useState<any>(null);
  const [writingUserInput, setWritingUserInput] = useState('');
  const [writingFeedback, setWritingFeedback] = useState<string | null>(null);
  const [isCorrectingWriting, setIsCorrectingWriting] = useState(false);
  const [savedWritingCorrectionSuccess, setSavedWritingCorrectionSuccess] = useState(false);
  const [isSavingWritingCorrection, setIsSavingWritingCorrection] = useState(false);

  useEffect(() => {
    setSelectedWordInfo(null);
  }, [currentIdx, cardFlipped]);

  useEffect(() => {
    const saved = localStorage.getItem('libritui_roadmap_completed_topics');
    if (saved) {
      try {
        setCompletedTopics(JSON.parse(saved));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (!user || !langCode || !level) return;
    
    let active = true;
    
    const loadProgress = async () => {
      try {
        const { dbManager } = await import('../services/db_manager');
        const results = await dbManager.from('roadmap_progress', user.id).select({ 
          language_code: langCode.toLowerCase() 
        });
        
        if (!active) return;
        
        if (results && results.length > 0) {
          setDbProgress(results[0]);
        } else {
          // No progress found, create and save default
          const defaultProg = {
            user_id: user.id,
            language_code: langCode.toLowerCase(),
            current_level: level.toUpperCase(),
            current_topic_index: 0
          };
          setDbProgress(defaultProg);
          await dbManager.from('roadmap_progress', user.id).upsert(defaultProg);
        }
      } catch (e) {
        console.error("Error loading roadmap progress:", e);
      }
    };
    
    loadProgress();
    
    return () => {
      active = false;
    };
  }, [user, langCode, level]);

  // Fetch all roadmaps for this user when on select screen
  useEffect(() => {
    if (!user || langCode || level) return;

    let active = true;
    const fetchCreatedRoadmaps = async () => {
      setLoadingCreated(true);
      try {
        const { dbManager } = await import('../services/db_manager');
        const results = await dbManager.from('roadmap_progress', user.id).select({
          user_id: user.id
        });
        
        if (!active) return;
        
        if (results && results.length > 0) {
          setCreatedRoadmaps(results);
        } else {
          // Default to English if no roadmaps are found in DB
          setCreatedRoadmaps([{
            language_code: 'en',
            current_level: 'A1',
            current_topic_index: 0
          }]);
        }
      } catch (err) {
        console.error("Error fetching created roadmaps from DB:", err);
      } finally {
        if (active) setLoadingCreated(false);
      }
    };

    fetchCreatedRoadmaps();

    return () => {
      active = false;
    };
  }, [user, langCode, level]);

  // Sort created roadmaps by advancement (highest CEFR level & highest topic index first)
  const sortedCreatedRoadmaps = useMemo(() => {
    if (!createdRoadmaps || !Array.isArray(createdRoadmaps)) return [];
    return [...createdRoadmaps].sort((a, b) => {
      const lvlA = CEFR_LEVELS.indexOf((a.current_level || 'A1').toUpperCase());
      const lvlB = CEFR_LEVELS.indexOf((b.current_level || 'A1').toUpperCase());
      if (lvlB !== lvlA) {
        return lvlB - lvlA;
      }
      const topicA = Number(a.current_topic_index || 0);
      const topicB = Number(b.current_topic_index || 0);
      return topicB - topicA;
    });
  }, [createdRoadmaps, CEFR_LEVELS]);

  // Map local native language settings
  const nativeFolder = useMemo(() => {
    const CODE_TO_FOLDER: Record<string, string> = {
      'en': 'ingles', 'fr': 'frances', 'de': 'alemao', 'pt': 'portugues',
      'it': 'italiano', 'es': 'espanhol', 'nl': 'holandes', 'ja': 'japones',
      'ru': 'russo', 'zh': 'chines', 'hu': 'hungaro'
    };
    const code = targetLanguage?.split('-')[0]?.toLowerCase() || 'pt';
    return CODE_TO_FOLDER[code] || 'portugues';
  }, [targetLanguage]);

  // Map study language (langCode) to study folder
  const studyFolder = useMemo(() => {
    const CODE_TO_FOLDER: Record<string, string> = {
      'en': 'ingles', 'fr': 'frances', 'de': 'alemao', 'pt': 'portugues',
      'it': 'italiano', 'es': 'espanhol', 'nl': 'holandes', 'ja': 'japones',
      'ru': 'russo', 'zh': 'chines', 'hu': 'hungaro'
    };
    return CODE_TO_FOLDER[langCode?.toLowerCase()] || 'ingles';
  }, [langCode]);

  // Load Roadmap Topics
  const roadmapNodes = useMemo(() => {
    const targetLangName = LANGUAGE_NAMES[langCode?.toLowerCase() || 'en'] || 'Inglês';
    const cleanLang = (langCode || 'en').toLowerCase();
    const cleanLevel = (level || 'a1').toLowerCase();

    // Get grammar labels if available
    const levelLabels = GRAMMAR_LABELS[cleanLang]?.[cleanLevel];
    
    const ICONS = [
      <Sparkles size={24} />,
      <User size={24} />,
      <FileText size={24} />,
      <BookOpen size={24} />,
      <Award size={24} />,
      <Compass size={24} />,
      <Bookmark size={24} />,
      <Star size={24} />,
      <Trophy size={24} />
    ];

    if (levelLabels && levelLabels.length > 0) {
      // Dynamic topics based on grammar modules count
      const grammarCount = levelLabels.length;
      const totalTopics = grammarCount * 2 + 1;
      const nodes = [];

      // Topic 1
      nodes.push({
        id: 'topic_1',
        label: `Tópico 1: Introdução ao ${targetLangName}`,
        icon: ICONS[0]
      });

      for (let i = 2; i <= totalTopics; i++) {
        const isGrammar = i % 2 !== 0;
        let label = '';
        if (isGrammar) {
          const grammarIdx = (i - 3) / 2;
          label = levelLabels[grammarIdx] || 'Gramática';
        } else {
          // Practice topics
          const practiceLabels = [
            'Frases do Dia a Dia',
            'Leitura e Fixação',
            'Revisão e Conversação',
            'Prática Ortográfica',
            'Vocabulário e Contexto',
            'Exercícios de Escuta'
          ];
          const practiceIdx = (i / 2 - 1) % practiceLabels.length;
          label = practiceLabels[practiceIdx];
        }

        nodes.push({
          id: `topic_${i}`,
          label: `Tópico ${i}: ${label}`,
          icon: ICONS[(i - 1) % ICONS.length]
        });
      }

      return nodes;
    }

    // Default/fallback for other languages (e.g. Spanish, German, etc. without grammar)
    const nodes = [];
    nodes.push({
      id: 'topic_1',
      label: `Tópico 1: Introdução ao ${targetLangName}`,
      icon: ICONS[0]
    });
    for (let i = 2; i <= 31; i++) {
      const isGrammar = i % 2 !== 0;
      let label = '';
      if (isGrammar) {
        label = 'Gramática Básica';
      } else {
        label = 'Prática e Fixação';
      }
      nodes.push({
        id: `topic_${i}`,
        label: `Tópico ${i}: ${label}`,
        icon: ICONS[(i - 1) % ICONS.length]
      });
    }
    return nodes;
  }, [langCode, level]);

  const isTopicCompleted = (nodeId: string) => {
    if (!user) {
      return completedTopics.includes(nodeId);
    }
    if (!dbProgress || !level) return false;
    
    const currentLvlUpper = level.toUpperCase();
    const dbLvlUpper = dbProgress.current_level.toUpperCase();
    
    const currentLvlIdx = CEFR_LEVELS.indexOf(currentLvlUpper);
    const dbLvlIdx = CEFR_LEVELS.indexOf(dbLvlUpper);
    
    if (dbLvlIdx > currentLvlIdx) {
      return true;
    }
    if (dbLvlIdx < currentLvlIdx) {
      return false;
    }
    
    const nodeIndex = roadmapNodes.findIndex(n => n.id === nodeId);
    return nodeIndex < dbProgress.current_topic_index;
  };

  const isTopicUnlocked = (nodeId: string) => {
    if (!user) {
      const nodeIndex = roadmapNodes.findIndex(n => n.id === nodeId);
      return nodeIndex === 0 || completedTopics.includes(roadmapNodes[nodeIndex - 1].id);
    }
    if (!dbProgress || !level) return nodeId === 'topic_1';
    
    const currentLvlUpper = level.toUpperCase();
    const dbLvlUpper = (dbProgress.current_level || 'A1').toUpperCase();
    
    const currentLvlIdx = CEFR_LEVELS.indexOf(currentLvlUpper);
    const dbLvlIdx = CEFR_LEVELS.indexOf(dbLvlUpper);

    // Topic 1 of any level equal to or lower than the user's unlocked level is ALWAYS unlocked
    if (nodeId === 'topic_1' && currentLvlIdx <= dbLvlIdx) {
      return true;
    }
    
    if (dbLvlIdx > currentLvlIdx) {
      const nodeIndex = roadmapNodes.findIndex(n => n.id === nodeId);
      return completedTopics.includes(nodeId) || (nodeIndex > 0 && completedTopics.includes(roadmapNodes[nodeIndex - 1].id));
    }
    if (dbLvlIdx < currentLvlIdx) {
      return false;
    }
    
    const nodeIndex = roadmapNodes.findIndex(n => n.id === nodeId);
    return nodeIndex <= dbProgress.current_topic_index;
  };

  // Auto scroll to active or last completed topic node on mount/viewState change
  useEffect(() => {
    if (viewState === 'roadmap') {
      // Find current active node (unlocked but not completed)
      let targetNodeId: string | null = null;
      const activeNode = roadmapNodes.find(n => isTopicUnlocked(n.id) && !isTopicCompleted(n.id));
      if (activeNode) {
        targetNodeId = activeNode.id;
      } else {
        // Fallback to last completed node
        const completedList = roadmapNodes.filter(n => isTopicCompleted(n.id));
        if (completedList.length > 0) {
          targetNodeId = completedList[completedList.length - 1].id;
        }
      }

      if (targetNodeId) {
        const nodeId = targetNodeId;
        setTimeout(() => {
          const el = document.getElementById(`node-${nodeId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [viewState, completedTopics, dbProgress, loading]);

  const triggerStreakUpdate = async (activityType: string) => {
    if (!user) return;
    try {
      const result = await streakService.validateAndIncrementStreak(user.id, activityType);
      if (result && result.newlyIncremented) {
        window.dispatchEvent(new CustomEvent('libritui-streak-updated', { detail: result }));
        refreshProfile?.(true);
      }
    } catch (e) {
      console.error("[Roadmap] Failed to trigger streak update:", e);
    }
  };

  const getGrammarFileName = (topicId: string, isEnglish: boolean) => {
    const num = parseInt(topicId.replace('topic_', ''), 10);
    const moduleIdx = (num - 3) / 2 + 1;
    const padIdx = String(moduleIdx).padStart(2, '0');
    
    const cleanLang = isEnglish ? 'en' : ((langCode || 'en').toLowerCase());
    const cleanLevel = (level || 'a1').toLowerCase();
    
    const langModules = GRAMMAR_MODULE_SUFFIXES[cleanLang] || GRAMMAR_MODULE_SUFFIXES.en;
    const levelModules = langModules[cleanLevel] || langModules.a1;
    const suffix = levelModules[moduleIdx - 1] || levelModules[0] || 'verb_to_be';
    
    return `${cleanLang}_${cleanLevel}_${padIdx}_${suffix}`;
  };

  const loadGrammarForTopic = async (topicId: string) => {
    const isEnglish = (langCode || 'en').toLowerCase() === 'en';
    const isFrench = (langCode || 'en').toLowerCase() === 'fr';
    const isItalian = (langCode || 'en').toLowerCase() === 'it';
    const newLangMeta = NEW_GRAMMAR_LANGUAGES[(langCode || 'en').toLowerCase()];

    if (!isEnglish && !isFrench && !isItalian && !newLangMeta) {
      setIsGrammarUnavailable(true);
      setGrammarView('explanation');
      setCurrentStepIdx(0);
      setExplanationSteps([]);
      setQuizQuestions([]);
      setExplanationFetchError(false);
      setLoading(false);
      return;
    }

    setIsGrammarUnavailable(false);
    setLoading(true);
    setGrammarView('explanation');
    setStudyModeChoice('select');
    setCurrentStepIdx(0);
    setQuizSessionId(crypto.randomUUID());
    setIsPageTranslated(false);
    setTranslatedSteps([]);
    setExplanationFetchError(false);
    
    try {
      const currentLang = (langCode || 'en').toLowerCase();
      const targetFolder = newLangMeta ? newLangMeta.folder : currentLang === 'en' ? 'english/language_english' : currentLang === 'it' ? 'italian/language_italian' : 'french/language_french';
      const targetPrefix = newLangMeta ? newLangMeta.prefix : currentLang === 'en' ? 'en' : currentLang === 'it' ? 'it' : 'fr';
      const levelKey = level ? level.toLowerCase() : 'a1';
      const expFileName = getGrammarFileName(topicId, currentLang === 'en');

      let expUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${targetFolder}/${levelKey}/${targetPrefix}_${levelKey}_explanation/${expFileName}.json`;
      let expRes = await fetch(expUrl);
      if (!expRes.ok) {
        const expFallback = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${targetFolder}/${levelKey}/${targetPrefix}_${levelKey}_explanation/${expFileName}_explanation.json`;
        expRes = await fetch(expFallback);
      }
      if (expRes.ok) {
        const expData = await expRes.json();
        setExplanationSteps(Array.isArray(expData) ? expData : [expData]);
      } else {
        setExplanationFetchError(true);
      }

      let quizUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${targetFolder}/${levelKey}/${targetPrefix}_${levelKey}_quizes/${expFileName}_quizes.json`;
      let quizRes = await fetch(quizUrl);
      if (!quizRes.ok) {
        const quizFallback = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/${targetFolder}/${levelKey}/${targetPrefix}_${levelKey}_quizes/${expFileName}.json`;
        quizRes = await fetch(quizFallback);
      }
      if (quizRes.ok) {
        const quizData = await quizRes.json();
        const rawQuestions = Array.isArray(quizData) ? quizData.slice(0, 10) : [];
        const randomized = rawQuestions.map((q: any) => {
          if (q && Array.isArray(q.options) && q.options.length > 0) {
            return {
              ...q,
              options: [...q.options].sort(() => 0.5 - Math.random())
            };
          }
          return q;
        });
        setQuizQuestions(randomized);
      }
    } catch (err) {
      console.error("Error fetching grammar content:", err);
      setExplanationFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadWritingForTopic = async (topicId: string) => {
    setLoading(true);
    setWritingStory(null);
    setWritingUserInput('');
    setWritingFeedback(null);
    setSavedWritingCorrectionSuccess(false);
    
    try {
      const isEnglish = (langCode || 'en').toLowerCase() === 'en';
      const targetSuffix = isEnglish ? 'en' : langCode?.toLowerCase() === 'pt' ? 'ptbr' : (langCode?.toLowerCase() || 'en');
      const nativeSuffix = (targetLanguage?.split('-')[0]?.toLowerCase() === 'pt' ? 'ptbr' : (targetLanguage?.split('-')[0]?.toLowerCase() || 'ptbr'));

      const targetUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-lessons/writing-content-${targetSuffix}.json`;
      const nativeUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-lessons/writing-content-${nativeSuffix}.json`;

      let targetData: any = null;
      let nativeData: any = null;

      const targetRes = await fetch(targetUrl);
      if (targetRes.ok) targetData = await targetRes.json();

      const nativeRes = await fetch(nativeUrl);
      if (nativeRes.ok) nativeData = await nativeRes.json();

      const levelKey = level ? level.toUpperCase() : 'A1';
      const targetStories = targetData?.escrita?.ingles?.[levelKey] || [];
      const nativeStories = nativeData?.escrita?.ingles?.[levelKey] || [];

      const num = parseInt(topicId.replace('topic_', ''), 10);
      const storyIdx = num - 4;

      const selectedTargetStory = targetStories[storyIdx];
      if (selectedTargetStory) {
        const matchingNativeStory = nativeStories.find((ns: any) => ns.id === selectedTargetStory.id);
        setWritingStory({
          ...selectedTargetStory,
          texto: selectedTargetStory.content || selectedTargetStory.texto || '',
          nativeTranslationText: matchingNativeStory?.content || matchingNativeStory?.texto || '',
          nativeTranslationTitle: matchingNativeStory?.title || matchingNativeStory?.titulo || ''
        });
      }

      // Fetch daily writing limit count from D1
      const isPro = profile && (profile.plan_type === 'pro' || profile.subscription_tier === 'pro');
      if (user && !isPro) {
        try {
          const todayDate = new Date().toISOString().split('T')[0];
          const { dbManager } = await import('../services/db_manager');
          const rows = await dbManager.from('user_daily_limits_writing', user.id).select({
            user_id: user.id,
            date: todayDate
          });
          if (rows && rows.length > 0) {
            setRoadmapWritingCount(rows[0].usage_count || 0);
          } else {
            setRoadmapWritingCount(0);
          }
        } catch (dbErr) {
          console.error("Error fetching daily writing usage from D1:", dbErr);
        }
      }
    } catch (err) {
      console.error("Error loading writing story for roadmap:", err);
    } finally {
      setLoading(false);
    }
  };

  const completeTopic = async () => {
    const activeTopic = currentTopicId || 'topic_1';
    const updated = [...new Set([...completedTopics, activeTopic])];
    setCompletedTopics(updated);
    localStorage.setItem('libritui_roadmap_completed_topics', JSON.stringify(updated));

    if (user && dbProgress && level && langCode) {
      try {
        const nodeIndex = roadmapNodes.findIndex(n => n.id === activeTopic);
        if (nodeIndex === dbProgress.current_topic_index) {
          let nextTopicIndex = dbProgress.current_topic_index;
          let nextLevel = dbProgress.current_level;

          if (nodeIndex < roadmapNodes.length - 1) {
            nextTopicIndex = nodeIndex + 1;
          } else {
            const currentLvlIdx = CEFR_LEVELS.indexOf(level.toUpperCase());
            if (currentLvlIdx < CEFR_LEVELS.length - 1) {
              nextLevel = CEFR_LEVELS[currentLvlIdx + 1];
              nextTopicIndex = 0;
            } else {
              nextTopicIndex = roadmapNodes.length;
            }
          }

          const updatedProg = {
            user_id: user.id,
            language_code: langCode.toLowerCase(),
            current_level: nextLevel,
            current_topic_index: nextTopicIndex
          };

          setDbProgress(updatedProg);

          const { dbManager } = await import('../services/db_manager');
          await dbManager.from('roadmap_progress', user.id).upsert(updatedProg);
        }

        // Record roadmap topic completion in logs_unique_words for target language
        const { uniqueWordsService } = await import('../services/uniqueWordsService');
        const topicTextList = exercises 
          ? exercises.map((e: any) => e.original || e.text || e.phrase || e.word || e.question || '').filter(Boolean) 
          : [];
        await uniqueWordsService.recordExerciseCompletion(
          user.id,
          langCode,
          'roadmap',
          activeTopic,
          topicTextList
        );
      } catch (e) {
        console.error("Failed to sync completed topic to D1:", e);
      }
    }

    const nodeIndex = roadmapNodes.findIndex(n => n.id === activeTopic);
    const isLastTopicOfLevel = nodeIndex >= 0 && nodeIndex === roadmapNodes.length - 1;

    if (isLastTopicOfLevel) {
      setShowLevelCompletionModal(true);
    } else {
      setShowCompletionModal(true);
    }
    setViewState('roadmap');
  };

  // Fetch exercises when practicing a Topic
  const startTopicPractice = async (nodeId: string) => {
    if (!nodeId.startsWith('topic_')) return;
    
    // Contabiliza a streak ao abrir um tópico no roadmap
    triggerStreakUpdate('roadmap_topic_open');

    setLoading(true);
    setViewState('practice');
    setCurrentIdx(0);
    setCardFlipped(false);
    setLastTranscript('');
    setRecognitionFeedback(null);
    setActiveTooltipNodeId(null);
    setCurrentTopicId(nodeId);

    // Reset overlay and transition states
    setIsCompletingTopic(false);
    setShowTransitionScreen(false);
    setCurrentExerciseIntroType(null);
    setShowWelcomeScreen(true);

    if (nodeId === 'topic_3') {
      setPracticeStage('grammar');
      await loadGrammarForTopic(nodeId);
      return;
    }

    if (nodeId === 'topic_4') {
      setPracticeStage('writing');
      await loadWritingForTopic(nodeId);
      return;
    }

    setPracticeStage('interleaved');

    try {
      let fcData: any[] = [];
      const levelKeyUpper = level ? level.toUpperCase() : 'A1';
      const targetKey = (langCode || 'en').toLowerCase() === 'pt' ? 'ptbr' : (langCode || 'en');
      const nativeKey = targetLanguage?.split('-')[0]?.toLowerCase() === 'pt' ? 'ptbr' : (targetLanguage?.split('-')[0]?.toLowerCase() || 'ptbr');

      if (levelKeyUpper === 'A1') {
        const R2_LANG_NAMES: Record<string, string> = {
          'de': 'Alemão',
          'es': 'Espanhol',
          'fr': 'Francês',
          'hu': 'Húngaro',
          'en': 'Inglês',
          'it': 'Italiano',
          'ptbr': 'Português',
          'ru': 'Russo'
        };
        
        const targetLangName = R2_LANG_NAMES[targetKey] || targetKey;
        const nativeLangName = R2_LANG_NAMES[nativeKey] || nativeKey;
        
        const targetUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/A1/${encodeURIComponent(targetLangName)} - A1.json?t=${Date.now()}`;
        const nativeUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/A1/${encodeURIComponent(nativeLangName)} - A1.json?t=${Date.now()}`;

        const [targetRes, nativeRes] = await Promise.all([
          fetch(targetUrl),
          fetch(nativeUrl)
        ]);

        if (!targetRes.ok) throw new Error(`Failed to download target language A1 deck`);
        if (!nativeRes.ok) throw new Error(`Failed to download native language A1 deck`);

        const targetData = await targetRes.json();
        const nativeData = await nativeRes.json();

        if (!Array.isArray(targetData) || !Array.isArray(nativeData)) {
          throw new Error('Invalid JSON format for split A1 decks');
        }

        fcData = targetData.map((item: any, idx: number) => {
          const nativeItem = nativeData[idx] || {};
          return {
            id_base: String(idx + 1),
            [targetKey]: item.text || '',
            [nativeKey]: nativeItem.text || ''
          };
        });
      } else {
        const fcUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/R2-consolidated-flashcards/words-counted-new/${levelKeyUpper}_Consolidado_Global.json?t=${Date.now()}`;
        const fcRes = await fetch(fcUrl);
        if (!fcRes.ok) throw new Error("Failed to fetch flashcards");
        fcData = await fcRes.json();
      }

      const num = parseInt(nodeId.replace('topic_', ''), 10);
      let startIndex = 0;
      if (nodeId === 'topic_2') {
        startIndex = 5;
      } else if (num >= 5) {
        startIndex = 10 + (num - 5) * 5;
      }
      const endIndex = startIndex + 5;

      const flashcardsSlice = fcData.slice(startIndex, endIndex).map((item: any, idx: number) => ({
        id: `roadmap_fc_${startIndex + idx}`,
        type: 'flashcard',
        original: item[targetKey] || item.en || '',
        translation: item[nativeKey] || item.ptbr || '',
      }));

      // 2. Fetch pronunciation exercises
      const pronUrl = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/pronuncia-escrita.json';
      const pronRes = await fetch(pronUrl);
      if (!pronRes.ok) throw new Error("Failed to fetch pronunciation exercises");
      const pronData = await pronRes.json();
      
      const levelKey = level ? level.toUpperCase() : 'A1';
      const studyCards = pronData?.pronuncia?.[studyFolder]?.[levelKey] || [];
      const nativeCards = pronData?.pronuncia?.[nativeFolder]?.[levelKey] || [];
      
      const pronunciationSlice = studyCards.slice(startIndex, endIndex).map((item: any, idx: number) => {
        const nativeMatch = nativeCards.find((n: any) => n.id === item.id);
        return {
          id: `roadmap_pron_${startIndex + idx}`,
          type: 'pronunciation',
          original: item.phrase || '',
          translation: nativeMatch?.phrase || '',
        };
      });

      // 3. First 5 flashcards, then 5 pronunciation (No longer interleaving)
      const concatenated = [...flashcardsSlice, ...pronunciationSlice];
      setExercises(concatenated);
    } catch (err) {
      console.error("[Roadmap] Failed to fetch practice content:", err);
    } finally {
      setLoading(false);
    }
  };

  const translateSingleGrammarStep = async (stepIndex: number) => {
    if (translatedSteps[stepIndex] || !explanationSteps[stepIndex]) return;

    setIsTranslatingPage(true);
    try {
      const targetLangCode = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'pt';
      const step = explanationSteps[stepIndex];
      const titleText = step.title || '';
      const explanationText = step.explanation || '';
      const tipText = step.tip || '';

      const resTitle = titleText ? await translationService.unifiedTranslate(titleText, targetLangCode, undefined, undefined, undefined, false, false, { isGrammar: true }) : { translation: '' };
      const resExplanation = explanationText ? await translationService.unifiedTranslate(explanationText, targetLangCode, undefined, undefined, undefined, false, false, { isGrammar: true }) : { translation: '' };
      const resTip = tipText ? await translationService.unifiedTranslate(tipText, targetLangCode, undefined, undefined, undefined, false, false, { isGrammar: true }) : { translation: '' };

      const translatedStep = {
        ...step,
        title: resTitle.translation || titleText,
        explanation: resExplanation.translation || explanationText,
        tip: resTip.translation || tipText
      };

      setTranslatedSteps(prev => ({
        ...prev,
        [stepIndex]: translatedStep
      }));
    } catch (err) {
      console.error("Failed to translate grammar step:", err);
    } finally {
      setIsTranslatingPage(false);
    }
  };

  const handleTranslatePage = async () => {
    if (isTranslatingPage) return;
    if (isPageTranslated) {
      setIsPageTranslated(false);
      return;
    }
    setIsPageTranslated(true);
    if (!translatedSteps[currentStepIdx]) {
      await translateSingleGrammarStep(currentStepIdx);
    }
  };

  useEffect(() => {
    if (isPageTranslated && explanationSteps.length > 0 && !translatedSteps[currentStepIdx]) {
      translateSingleGrammarStep(currentStepIdx);
    }
  }, [currentStepIdx, isPageTranslated, explanationSteps]);

  // Quiz Translation
  const translateCurrentQuizQuestion = async (qIdx: number) => {
    const currentQ = quizQuestions[qIdx];
    if (!currentQ) return;

    const targetLangCode = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'pt';
    const sourceLangCode = (langCode || 'en').split('-')[0].toLowerCase();

    setIsTranslatingQuiz(true);
    try {
      if (!translatedQuizPrompts[qIdx]) {
        const rawPrompt = currentQ.question_text || currentQ.question || currentQ.prompt || currentQ.sentence || currentQ.title || currentQ.text || '';
        const translatedP = await translateQuizPrompt(rawPrompt, targetLangCode, sourceLangCode);
        setTranslatedQuizPrompts(prev => ({ ...prev, [qIdx]: translatedP }));
      }

      if (currentQ.wrong_explanation && !translatedQuizExplanations[qIdx]) {
        const translatedE = await translateQuizExplanation(currentQ.wrong_explanation, targetLangCode, sourceLangCode);
        setTranslatedQuizExplanations(prev => ({ ...prev, [qIdx]: translatedE }));
      }
    } catch (err) {
      console.error("[QuizTranslation] Error in RoadmapPage:", err);
    } finally {
      setIsTranslatingQuiz(false);
    }
  };

  const handleToggleQuizTranslation = async () => {
    if (isTranslatingQuiz) return;
    if (isQuizTranslated) {
      setIsQuizTranslated(false);
      return;
    }
    setIsQuizTranslated(true);
    await translateCurrentQuizQuestion(currentQuestionIdx);
  };

  useEffect(() => {
    if (isQuizTranslated && quizQuestions.length > 0 && currentQuestionIdx < quizQuestions.length) {
      translateCurrentQuizQuestion(currentQuestionIdx);
    }
  }, [currentQuestionIdx, isQuizTranslated, quizQuestions]);

  const handleFlashcardRating = async (quality: 'again' | 'hard' | 'good' | 'easy') => {
    const currentCard = exercises[currentIdx];
    if (!currentCard) return;

    // Contabiliza a streak ao responder/avaliar flashcard no roadmap
    triggerStreakUpdate('roadmap_flashcard');

    if (user) {
      try {
        const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
        const cardObj = {
          id: currentCard.id,
          lang_code: cleanLang,
          deck_id: currentTopicId || 'topic_1'
        };
        const { flashcardManager } = await import('../services/flashcardManager');
        await flashcardManager.trackFeedbackHistory(cardObj as any, quality);

        const { statsService } = await import('../services/statsService');
        await statsService.increment('cards_reviewed', user.id, 1, { langCode: cleanLang });
        
        import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
          uniqueWordsService.recordExerciseCompletion(
            user.id,
            cleanLang,
            'flashcards',
            currentCard.id || `card_${currentIdx}`,
            currentCard.original || currentCard.text || ''
          );
        });

        if (quality === 'again' || quality === 'hard') {
          import('../services/errorReviewService').then(({ errorReviewService }) => {
            errorReviewService.recordError({
              userId: user.id,
              language: cleanLang,
              exerciseType: 'flashcards',
              sourceOrigin: 'roadmap',
              itemId: currentCard.id || `card_${currentIdx}`,
              itemPayload: currentCard,
              userInput: quality
            });
          });
        }
      } catch (err) {
        console.error("Failed to save flashcard feedback:", err);
      }
    }
    
    nextExercise();
  };

  const handleWritingAICorrect = async () => {
    if (isCorrectingWriting || !writingUserInput.trim() || !writingStory) return;

    // Contabiliza a streak ao enviar o texto de escrita para correção
    triggerStreakUpdate('roadmap_writing');

    setWritingFeedback(null);
    setSavedWritingCorrectionSuccess(false);
    setIsCorrectingWriting(true);

    const actualOriginal = writingStory.texto || '';
    const actualUserTranslation = writingUserInput.trim();
    const referenceTranslation = writingStory.nativeTranslationText || '';

    try {
      const { groqService } = await import('../services/groqService');
      const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
      const levelKey = level ? level.toUpperCase() : 'A1';
      
      const mappedNativeLang = 'portuguese';

      const result = await groqService.generateFeedbackViaProxy(
        actualOriginal,
        actualUserTranslation,
        mappedNativeLang,
        levelKey,
        'traducao',
        referenceTranslation,
        writingStory.id
      );

      setWritingFeedback(result);

      if (user) {
        import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
          uniqueWordsService.recordExerciseCompletion(
            user.id,
            cleanLang,
            'writing',
            `writing_${currentTopicId}`,
            actualOriginal
          );
        });
      }

      // Extract score and save if <= 6
      const scoreMatch = result.match(/(\d+(?:\.\d+)?)\s*\/\s*10/);
      if (scoreMatch) {
        const score = parseFloat(scoreMatch[1]);
        if (score <= 6) {
          if (user) {
            import('../services/errorReviewService').then(({ errorReviewService }) => {
              errorReviewService.recordError({
                userId: user.id,
                language: cleanLang,
                exerciseType: 'writing',
                sourceOrigin: 'roadmap',
                itemId: `writing_${currentTopicId}`,
                itemPayload: { prompt: actualOriginal, expected: referenceTranslation },
                userInput: actualUserTranslation
              });
            });
          }
          let grammarReport = null;
          const grammarMatch = result.match(/5\.\s*[A-Za-zÀ-ÖØ-öø-ÿ]+:\s*([\s\S]*)/i);
          if (grammarMatch && grammarMatch[1]) {
            grammarReport = grammarMatch[1].trim();
          }

          const saveCorrection = async () => {
            try {
              const { supabase } = await import('../services/supabaseClient');
              const { data: { session } } = await supabase.auth.getSession();
              const token = session?.access_token;

              await fetch('/api/writing-corrections', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                  id: crypto.randomUUID(),
                  user_id: user ? user.id : "NULL",
                  exercise_mode: 'translation',
                  target_lang: cleanLang,
                  native_lang: 'pt',
                  original_text: actualOriginal,
                  user_input: actualUserTranslation,
                  ai_correction: result,
                  score: score,
                  grammar_report: grammarReport,
                  created_at: getLocalISOString()
                })
              });
            } catch (e) {
              console.error('[Roadmap] Writing low score save failed:', e);
            }
          };
          saveCorrection();
        }
      }

      // Increment daily writing limit count locally if user is free
      const isProUser = profile && (profile.plan_type === 'pro' || profile.subscription_tier === 'pro');
      if (!isProUser) {
        setRoadmapWritingCount(prev => prev + 1);
      }

      // STATISTICS: Increment words written statistic
      if (user) {
        import('../services/statsService').then(({ statsService }) => {
          statsService.increment('words_written', user.id, 1, { langCode: cleanLang });
        });
      }

    } catch (err: any) {
      console.error("Error evaluating writing correction:", err);
      if (err.message === "Limite de 5 mensagens atingido." || (err.message && err.message.includes("Limite de 5 mensagens atingido"))) {
        setShowLimitModal(true);
        setRoadmapWritingCount(5);
      }
    } finally {
      setIsCorrectingWriting(false);
    }
  };

  const handleSaveWritingCorrection = async () => {
    if (!user || isSavingWritingCorrection || savedWritingCorrectionSuccess || !writingStory || !writingFeedback) return;
    setIsSavingWritingCorrection(true);
    try {
      const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
      const { supabase } = await import('../services/supabaseClient');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const scoreMatch = writingFeedback.match(/(\d+(?:\.\d+)?)\s*\/\s*10/);
      const score = scoreMatch ? parseFloat(scoreMatch[1]) : 7.0;
      
      let grammarReport = null;
      const grammarMatch = writingFeedback.match(/5\.\s*[A-Za-zÀ-ÖØ-öø-ÿ]+:\s*([\s\S]*)/i);
      if (grammarMatch && grammarMatch[1]) {
        grammarReport = grammarMatch[1].trim();
      }

      const res = await fetch('/api/writing-corrections', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          id: crypto.randomUUID(),
          user_id: user.id,
          exercise_mode: 'translation',
          target_lang: cleanLang,
          native_lang: 'pt',
          original_text: writingStory.texto || '',
          user_input: writingUserInput.trim(),
          ai_correction: writingFeedback,
          score: score,
          grammar_report: grammarReport,
          created_at: getLocalISOString()
        })
      });

      if (res.ok) {
        setSavedWritingCorrectionSuccess(true);
      }
    } catch (e) {
      console.error('[Roadmap] Manual save failed:', e);
    } finally {
      setIsSavingWritingCorrection(false);
    }
  };

  // TTS recitation
  const speakCurrent = (text: string) => {
    textToSpeechService.speak(text, langCode || 'en', 0.95);
  };

  // Recitation with speed rate
  const handleSpeakWithRate = async (text: string, lang: string = 'en') => {
    if (isTtsPlaying) {
      textToSpeechService.stop();
      setIsTtsPlaying(false);
      return;
    }
    setIsTtsPlaying(true);
    try {
      await textToSpeechService.speak(text, lang, ttsRate);
    } catch (err) {
      console.error("Audio playback error:", err);
    } finally {
      setIsTtsPlaying(false);
    }
  };

  // Speech Recognition handler
  const toggleListening = async (textToMatch: string) => {
    if (isListening) {
      stopListening();
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setLastTranscript('Seu navegador não suporta reconhecimento de voz.');
        return;
      }

      const recognition = new SpeechRecognition();
      const targetLang = (langCode || 'en').toLowerCase();
      recognition.lang = targetLang === 'fr' ? 'fr-FR' : 
                         targetLang === 'de' ? 'de-DE' : 
                         targetLang === 'es' ? 'es-ES' : 
                         targetLang === 'it' ? 'it-IT' : 
                         targetLang === 'pt' ? 'pt-BR' : 
                         targetLang === 'ru' ? 'ru-RU' : 
                         targetLang === 'hu' ? 'hu-HU' : 
                         'en-US';
      recognition.interimResults = true;
      recognition.continuous = true;

      recognition.onstart = () => {
        setIsListening(true);
        setTimeLeft(15);
        setRecognitionFeedback(null);
        setLastTranscript('');
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              if (recognitionRef.current) {
                try { recognitionRef.current.stop(); } catch (e) {}
              }
              setIsListening(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      };

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((res: any) => res[0].transcript)
          .join('');
        setLastTranscript(transcript);
      };

      recognition.onerror = (e: any) => {
        console.error("Speech Error:", e.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
        // Compare result
        setLastTranscript(prev => {
          const finalTranscript = prev.trim();
          if (finalTranscript) {
            const cleanTarget = normalizeForComparison(textToMatch);
            const cleanTranscript = normalizeForComparison(finalTranscript);
            const score = getSimilarity(cleanTarget, cleanTranscript);
            
            // Contabiliza a streak ao tentar a pronúncia no roadmap
            triggerStreakUpdate('roadmap_pronunciation');
            
            if (score >= 0.90) {
              setRecognitionFeedback('success');
              new Audio(SUCCESS_SOUND_URL).play().catch(() => {});
              if (user) {
                const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
                let hash = 0;
                for (let i = 0; i < textToMatch.length; i++) {
                  hash = ((hash << 5) - hash) + textToMatch.charCodeAt(i);
                  hash = hash & hash;
                }
                const persistentId = `roadmap_pron_${Math.abs(hash)}`;

                import('../services/statsService').then(({ statsService }) => {
                  statsService.increment('pronunciation_count', user.id, 1, { langCode: cleanLang });
                });
                import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
                  uniqueWordsService.recordExerciseCompletion(
                    user.id,
                    cleanLang,
                    'pronunciation',
                    persistentId,
                    textToMatch
                  );
                });
              }
            } else {
              setRecognitionFeedback('error');
              if (user) {
                const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
                let hash = 0;
                for (let i = 0; i < textToMatch.length; i++) {
                  hash = ((hash << 5) - hash) + textToMatch.charCodeAt(i);
                  hash = hash & hash;
                }
                const persistentId = `roadmap_pron_${Math.abs(hash)}`;
                const currentEx = exercises[currentIdx] || {};
                const translationText = currentEx.translation || '';

                import('../services/errorReviewService').then(({ errorReviewService }) => {
                  errorReviewService.recordError({
                    userId: user.id,
                    language: cleanLang,
                    exerciseType: 'pronunciation',
                    sourceOrigin: 'roadmap',
                    itemId: persistentId,
                    itemPayload: { phrase: textToMatch, translation: translationText },
                    userInput: finalTranscript
                  });
                });

                import('../services/db_manager').then(({ dbManager }) => {
                  const uniqueId = `roadmap_pron_${Math.abs(hash)}_${Date.now()}`;
                  dbManager.from('user_pronunciation_corrections').insert({
                    id: uniqueId,
                    user_id: user.id,
                    original_text: textToMatch,
                    user_speech_text: finalTranscript,
                    status_color: 'red',
                    target_lang: cleanLang,
                    created_at: getLocalISOString()
                  }).catch(e => console.warn('[Roadmap] user_pronunciation_corrections insert warning:', e));
                });
              }
            }
          }
          return prev;
        });
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error("Mic Permission error:", err);
    }
  };

  const stopListening = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    setIsListening(false);
    setTimeLeft(15);
  };

  // Welcome, Intro, and Transition helper functions
  const handleConfirmWelcome = () => {
    setShowWelcomeScreen(false);
    // Show first exercise type intro
    if (currentTopicId === 'topic_3') {
      setActiveIntroIndex(Math.floor(Math.random() * 3));
      setCurrentExerciseIntroType('grammar');
    } else if (currentTopicId === 'topic_4') {
      setActiveIntroIndex(Math.floor(Math.random() * 3));
      setCurrentExerciseIntroType('writing');
    } else {
      setActiveIntroIndex(Math.floor(Math.random() * 3));
      setCurrentExerciseIntroType('flashcard');
    }
  };

  const showMotivationalTransition = (onConfirm: () => void) => {
    const lang = i18n.language || 'pt';
    const cleanLang = lang.split('-')[0].toLowerCase();
    const trans = LOCAL_TRANSLATIONS[cleanLang] || LOCAL_TRANSLATIONS['pt'] || LOCAL_TRANSLATIONS['en'];
    const phrases = trans.motivationalPhrases || [];
    const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)] || "Parabéns pelo progresso!";
    
    setActiveTransitionPhrase(randomPhrase);
    setOnConfirmTransition(() => onConfirm);
    setShowTransitionScreen(true);
  };

  // Practice navigation
  const nextExercise = async () => {
    const isOddTopic = (topicId: string) => {
      const num = parseInt(topicId.replace('topic_', ''), 10);
      return !isNaN(num) && num > 4 && num % 2 !== 0;
    };
    const isEvenTopic = (topicId: string) => {
      const num = parseInt(topicId.replace('topic_', ''), 10);
      return !isNaN(num) && num > 4 && num % 2 === 0;
    };

    if (practiceStage === 'interleaved') {
      if (currentIdx < exercises.length - 1) {
        const nextIdx = currentIdx + 1;
        // If moving from flashcards (indices 0-4) to pronunciation (indices 5-9)
        if (nextIdx === 5) {
          setActiveIntroIndex(Math.floor(Math.random() * 3));
          setCurrentExerciseIntroType('pronunciation');
        }
        setCurrentIdx(nextIdx);
        setCardFlipped(false);
        setLastTranscript('');
        setRecognitionFeedback(null);
      } else {
        // Finished interleaved exercises!
        const topicId = currentTopicId || '';
        if (isOddTopic(topicId)) {
          // Show transition screen, then go to grammar stage
          showMotivationalTransition(async () => {
            setPracticeStage('grammar');
            setActiveIntroIndex(Math.floor(Math.random() * 3));
            setCurrentExerciseIntroType('grammar');
            await loadGrammarForTopic(topicId);
          });
        } else if (isEvenTopic(topicId)) {
          // Show transition screen, then go to writing stage
          showMotivationalTransition(async () => {
            setPracticeStage('writing');
            setActiveIntroIndex(Math.floor(Math.random() * 3));
            setCurrentExerciseIntroType('writing');
            await loadWritingForTopic(topicId);
          });
        } else {
          // Normal topic (topic_1, topic_2) - finished!
          await completeTopic();
        }
      }
    } else if (practiceStage === 'grammar') {
      const topicId = currentTopicId || '';
      if (isOddTopic(topicId)) {
        // Show transition screen, then go to writing stage
        showMotivationalTransition(async () => {
          setPracticeStage('writing');
          setActiveIntroIndex(Math.floor(Math.random() * 3));
          setCurrentExerciseIntroType('writing');
          await loadWritingForTopic(topicId);
        });
      } else {
        // topic_3 - finished!
        await completeTopic();
      }
    } else if (practiceStage === 'writing') {
      // Completed writing stage - finished!
      await completeTopic();
    }
  };

  // If no language selected (e.g. url path does not match A1 / Inglês yet)
  if (!langCode || !level) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-ink p-4 sm:p-8 flex flex-col items-center justify-center animate-in fade-in duration-300 pb-36 pt-8 overflow-y-auto relative">
        {/* Top-Left Back to Home Button */}
        <div className="absolute top-6 left-6 z-30">
          <AppBackButton 
            fallbackUrl="/study-hub"
          />
        </div>

        <div className="w-full max-w-md p-6 sm:p-8 rounded-[28px] bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-8 text-center relative my-auto mt-16 sm:mt-auto">
          <div className="absolute inset-0 rounded-[28px] overflow-hidden pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/5 rounded-full" />
          </div>
          
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-tint-sky text-ink flex items-center justify-center">
              <AnimatedIcon motion="spin" active><Compass size={28} strokeWidth={1.5} /></AnimatedIcon>
            </div>
          </div>

          <div className="space-y-2 text-center">
            <h1 className="font-display text-[40px] font-light tracking-[-0.045em] leading-none text-ink capitalize">{String(t('hubs.roadmap_title', 'TRILHA')).toLowerCase()}<span className="text-primary">.</span></h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs font-normal uppercase font-mono tracking-[0.14em] leading-relaxed">
              {t('hubs.roadmap_subtitle', 'SEU ROTEIRO DE ESTUDOS, CONQUISTAS E NÍVEIS')}
            </p>
          </div>

          {loadingCreated ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Dropdown "Selecionar" para idiomas já abertos */}
              {sortedCreatedRoadmaps.length > 0 && (
                <div className="relative w-full">
                  <div className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 text-left mb-2 px-1">
                    Seus Idiomas em Andamento
                  </div>
                  <button 
                    type="button"
                    onClick={() => setShowLangMenu(!showLangMenu)}
                    className="w-full p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] bg-slate-50 dark:bg-slate-950/60 hover:bg-slate-100 dark:hover:bg-slate-950 transition-all flex items-center justify-between cursor-pointer"
                  >
                    <span className="font-bold text-sm text-ink dark:text-slate-200">Selecionar Idioma</span>
                    <ChevronDown size={18} className="text-slate-500" />
                  </button>

                  {showLangMenu && (
                    <div 
                      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setShowLangMenu(false)}
                    >
                      <div 
                        className="w-full max-w-sm bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-3xl p-5 space-y-3 animate-in zoom-in-95 duration-150 max-h-[75vh] flex flex-col text-left"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between pb-3 border-b border-black/[0.06] dark:border-white/[0.06] px-1">
                          <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400">
                            Seus Idiomas em Andamento
                          </span>
                          <button 
                            type="button"
                            onClick={() => setShowLangMenu(false)}
                            className="p-1 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors cursor-pointer"
                          >
                            <X size={18} />
                          </button>
                        </div>

                        <div className="overflow-y-auto space-y-2 pr-1 max-h-[55vh] flex-1">
                          {sortedCreatedRoadmaps.map((r: any) => {
                            const code = r.language_code.toLowerCase();
                            const name = LANGUAGE_NAMES[code] || code.toUpperCase();
                            const flag = LANGUAGE_FLAGS[code] || '🌐';
                            const lvl = (r.current_level || 'A1').toUpperCase();
                            return (
                              <button 
                                key={code}
                                onClick={() => {
                                  setShowLangMenu(false);
                                  navigate(`/roadmap/${code}/${lvl.toLowerCase()}`);
                                }}
                                className="w-full p-3.5 rounded-2xl border border-black/[0.06] dark:border-slate-800/60 bg-slate-50/80 dark:bg-slate-950/60 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center justify-between text-left cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl select-none">{flag}</span>
                                  <div>
                                    <span className="font-bold text-sm block text-ink">{name}</span>
                                    <span className="text-[10px] font-normal text-slate-500 uppercase font-mono tracking-[0.14em] block">Nível {lvl}</span>
                                  </div>
                                </div>
                                <ArrowRight size={16} className="text-slate-400" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Botão Adicionar Novo Idioma */}
              {!showAddLangSelector && (
                <button 
                  onClick={() => setShowAddLangSelector(true)}
                  className="w-full py-4 border border-dashed border-slate-300 dark:border-slate-700/60 hover:border-slate-900 dark:hover:border-white transition-all rounded-2xl flex items-center justify-center gap-2 cursor-pointer group"
                >
                  <Plus size={20} className="text-slate-500 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors" />
                  <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white">Adicionar Novo Idioma</span>
                </button>
              )}

              {showAddLangSelector && (
                <div className="space-y-3 p-4 rounded-3xl bg-slate-50 dark:bg-slate-950/60 border border-black/[0.06] dark:border-white/[0.06] animate-in slide-in-from-bottom-2 duration-200 max-w-full overflow-hidden">
                  <div className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2">Selecione o Idioma</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(LANGUAGE_NAMES)
                      .filter(code => code !== nativeLangCode && !createdRoadmaps.some(r => r.language_code.toLowerCase() === code))
                      .map(code => (
                        <button
                          key={code}
                          onClick={() => {
                            setShowAddLangSelector(false);
                            setSelectedNewLangModal(code);
                          }}
                          className="p-3 bg-surface border border-black/[0.06] dark:border-white/[0.06] hover:border-slate-400 rounded-xl flex items-center gap-2 text-left transition-all cursor-pointer"
                        >
                          <span className="text-2xl select-none">{LANGUAGE_FLAGS[code]}</span>
                          <span className="text-xs font-bold text-ink">{LANGUAGE_NAMES[code]}</span>
                        </button>
                      ))
                    }
                  </div>
                  <button
                    onClick={() => setShowAddLangSelector(false)}
                    className="w-full mt-2 py-2 text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-bad hover:text-bad dark:text-bad transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal: Pergunta de conhecimento prévio do idioma */}
        {selectedNewLangModal && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-3xl p-6 max-w-sm w-full text-center space-y-6 animate-in zoom-in-95 duration-200 relative">
              <button
                onClick={() => setSelectedNewLangModal(null)}
                className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all cursor-pointer"
                title="Fechar"
              >
                <X size={18} />
              </button>

              <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 text-ink rounded-2xl flex items-center justify-center mx-auto text-3xl">
                {LANGUAGE_FLAGS[selectedNewLangModal] || '🌐'}
              </div>
              <div className="space-y-2">
                <h3 className="text-base font-semibold text-ink leading-relaxed">
                  {t('roadmap.knowledge_prompt', 'Você já possui conhecimento neste idioma ou deseja começar do zero?')}
                </h3>
              </div>
              <div className="space-y-3 pt-2">
                <button
                  onClick={() => {
                    const lang = selectedNewLangModal;
                    setSelectedNewLangModal(null);
                    navigate(`/roadmap/${lang}/a1`);
                  }}
                  className="text-[13px] font-medium w-full h-12 bg-slate-100 dark:bg-slate-800 hover:bg-tint-stone text-ink rounded-xl transition-all border border-black/[0.06] dark:border-slate-700 cursor-pointer"
                >
                  {t('roadmap.start_from_zero', 'Começar do zero')}
                </button>
                <button
                  onClick={() => {
                    const lang = selectedNewLangModal;
                    setSelectedNewLangModal(null);
                    navigate(`/onboarding?targetLang=${lang}`);
                  }}
                  className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                >
                  {t('roadmap.already_study', 'Sim, já estudo!')}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-500 select-none pb-20">
      
      {/* Top Header Selector & Sticky Status Bar */}
      {viewState === 'roadmap' && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-slate-50/95 dark:bg-slate-950/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-900/80">
          <div className="px-6 py-3 flex items-center justify-between">
            <AppBackButton 
              fallbackUrl="/roadmap"
            />
            
            <div className="flex items-center gap-3">
              {/* Lang Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => { setShowLangMenu(!showLangMenu); setShowLevelMenu(false); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/[0.06] dark:border-white/[0.06] text-xs font-semibold bg-surface hover:bg-slate-100 transition-all"
                >
                  <span>{LANGUAGE_FLAGS[langCode] || '🌐'} {LANGUAGE_NAMES[langCode] || langCode.toUpperCase()}</span>
                  <ChevronDown size={14} />
                </button>
                {showLangMenu && (
                  <div className="absolute right-0 mt-2 w-48 rounded-2xl bg-surface border border-black/[0.06] dark:border-white/[0.06] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] p-2 z-50 animate-in slide-in-from-top-2 duration-150">
                    {Object.keys(LANGUAGE_NAMES)
                      .filter(code => code !== nativeLangCode)
                      .map(code => (
                        <button 
                          key={code}
                          onClick={() => { setShowLangMenu(false); navigate(`/roadmap/${code}/a1`); }}
                          className="w-full flex items-center gap-2 p-2.5 rounded-xl text-left text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        >
                          <span className="select-none">{LANGUAGE_FLAGS[code]}</span>
                          <span>{LANGUAGE_NAMES[code]}</span>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>

              {/* Level Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => { setShowLevelMenu(!showLevelMenu); setShowLangMenu(false); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/[0.06] dark:border-white/[0.06] text-xs font-semibold bg-surface hover:bg-slate-100 transition-all"
                >
                  <span>{level.toUpperCase()}</span>
                  <ChevronDown size={14} />
                </button>
                {showLevelMenu && (
                  <div className="absolute right-0 mt-2 w-28 rounded-2xl bg-surface border border-black/[0.06] dark:border-white/[0.06] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] p-2 z-50 animate-in slide-in-from-top-2 duration-150">
                    {['a1', 'a2', 'b1', 'b2', 'c1', 'c2'].map((lvl) => (
                      <button 
                        key={lvl}
                        onClick={() => {
                          setShowLevelMenu(false);
                          navigate(`/roadmap/${langCode}/${lvl}`);
                        }}
                        className={`w-full p-2.5 rounded-xl text-center text-xs font-semibold ${lvl === level ? 'bg-primary/10 text-primary' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                      >
                        {lvl.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full max-w-4xl mx-auto px-4 pb-2.5">
            <TopStatsBar activeLangOverride={langCode} />
          </div>
        </div>
      )}

      {/* Main Roadmap Tree Track */}
      {viewState === 'roadmap' && (
        <div 
          ref={scrollContainerRef}
          className="pt-44 pb-44 px-6 flex flex-col-reverse items-center justify-start gap-24 relative overflow-y-auto max-w-lg mx-auto"
          onClick={() => setActiveTooltipNodeId(null)}
        >
          {roadmapNodes.map((node, index) => {
            const completed = isTopicCompleted(node.id);
            const unlocked = isTopicUnlocked(node.id);
            const current = unlocked && !completed;
            
            // alternating horizontal waviness
            const xOffset = Math.sin(index * 1.5) * 50; 
            const prevXOffset = index > 0 ? Math.sin((index - 1) * 1.5) * 50 : 0;
            
            // svg connecting path lines
            const dx = prevXOffset - xOffset;
            const svgWidth = Math.abs(dx) + 80;
            const svgLeft = Math.min(0, dx);
            const startX = 40 - svgLeft;
            const endX = 40 + dx - svgLeft;

            let bgColor = 'bg-tint-stone';
            let ringColor = 'ring-tint-stone/40';
            let shadowColor = '';
            let opacity = 'opacity-50';

            if (completed) {
              bgColor = 'bg-primary';
              ringColor = 'ring-primary/20';
              shadowColor = 'shadow-[0_4px_0_#B85E2C]';
              opacity = 'opacity-100';
            } else if (current) {
              bgColor = 'bg-surface border-2 border-primary';
              ringColor = 'ring-primary/25';
              shadowColor = 'shadow-[0_0_28px_rgba(232,130,63,0.35)]';
              opacity = 'opacity-100';
            }

            const isTooltipActive = activeTooltipNodeId === node.id;

            const cleanLvlLower = (level || 'a1').toLowerCase();
            const cleanNative = (i18n.language || 'pt').split('-')[0].toLowerCase();
            const bonusDefsForLevel = ROADMAP_BONUS_DEFS[cleanLvlLower] || [];
            const bonusDefForNode = bonusDefsForLevel.find(b => b.afterTopicIndex === index);

            return (
              <div 
                key={node.id}
                id={`node-${node.id}`}
                className={`relative flex flex-col items-center group ${opacity} ${isTooltipActive ? 'z-50' : 'z-10'}`}
                style={{ transform: `translateX(${xOffset}px)` }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (unlocked) {
                    setActiveTooltipNodeId(isTooltipActive ? null : node.id);
                  } else {
                    setBlockedTopicModalContent({
                      title: localT.blockedTopicTitle || "Tópico Bloqueado!",
                      message: localT.blockedTopicMsg || "Complete os tópicos anteriores para desbloquear."
                    });
                    setShowBlockedTopicModal(true);
                  }
                }}
              >
                {/* Lateral Bonus Topic Node */}
                {bonusDefForNode && (
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 z-30 flex items-center pointer-events-auto"
                    style={{
                      left: xOffset > 0 ? '-135px' : '95px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg 
                      className="absolute top-1/2 -translate-y-1/2 -z-10 pointer-events-none"
                      style={{
                        width: 35,
                        height: 12,
                        left: xOffset > 0 ? 'auto' : '-35px',
                        right: xOffset > 0 ? '-35px' : 'auto'
                      }}
                      viewBox="0 0 35 12"
                    >
                      <line 
                        x1="0" y1="6" x2="35" y2="6" 
                        stroke="#E8823F" 
                        strokeWidth="3" 
                        strokeDasharray="4 3" 
                      />
                    </svg>

                    <button
                      onClick={() => handleBonusTopicClick(bonusDefForNode)}
                      className="relative group transition-all duration-200 hover:scale-105 active:scale-95 flex flex-col items-center cursor-pointer opacity-100"
                      title={bonusDefForNode.title[cleanNative] || bonusDefForNode.title['pt']}
                    >
                      <div className="w-14 h-14 rounded-2xl bg-primary p-0.5 ring-4 ring-primary/15">
                        <div className="w-full h-full bg-surface rounded-[14px] flex items-center justify-center relative overflow-hidden">
                          <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.75} />
                          <div className="absolute top-0 right-0 bg-primary text-[8px] font-medium text-slate-900 px-1 rounded-bl-md uppercase tracking-[0.14em] font-mono">
                            PRO
                          </div>
                        </div>
                      </div>

                      <span className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-primary whitespace-nowrap">
                        {({ pt: 'Bônus', hu: 'Bónusz', ru: 'Бонус', ja: 'ボーナス', zh: '奖励' } as Record<string, string>)[cleanNative] || 'Bonus'} {bonusDefForNode.bonusNumber}
                      </span>
                    </button>
                  </div>
                )}
                {/* SVG connection path (drawn above the node if not first node) */}
                {index > 0 && (
                  <svg 
                    className="absolute -bottom-24 -z-10 pointer-events-none"
                    style={{
                      width: svgWidth,
                      height: 96,
                      left: svgLeft
                    }}
                    viewBox={`0 0 ${svgWidth} 96`}
                    preserveAspectRatio="none"
                  >
                    <path 
                      d={`M ${startX} 0 C ${startX} 48, ${endX} 48, ${endX} 96`}
                      fill="none"
                      stroke={completed || current ? '#E8823F' : '#9AA3B0'}
                      strokeOpacity={completed || current ? 0.9 : 0.35}
                      strokeWidth="6"
                      strokeDasharray="0 18"
                      strokeLinecap="round"
                    />
                  </svg>
                )}

                {/* Node Button Circle */}
                <button 
                  className={`w-20 h-20 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 relative transition-transform ring-8 duration-200 cursor-pointer ${bgColor} ${ringColor} ${shadowColor} ${isTooltipActive ? 'scale-110' : 'hover:scale-105 active:scale-95'}`}
                >
                  {current && (
                    <div className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-40" />
                  )}
                  
                  <div className={completed ? 'text-slate-900' : current ? 'text-primary' : 'text-muted'}>
                    {node.icon}
                  </div>

                  {completed && (
                    <div className="absolute -top-1.5 -right-1.5 bg-surface text-primary rounded-full p-1 border border-primary/40">
                      <Check size={12} strokeWidth={4} />
                    </div>
                  )}

                  {!unlocked && (
                    <div className="absolute -top-1 -right-1 bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 rounded-full p-1 border border-white dark:border-white/[0.06]">
                      <Lock size={12} />
                    </div>
                  )}
                </button>

                {/* Node Tooltip Card */}
                {isTooltipActive && (
                  <div className="absolute -top-24 flex flex-col items-center z-50 pointer-events-auto">
                    <div className="bg-surface rounded-2xl border border-black/[0.06] dark:border-white/[0.06] p-4 flex flex-col items-center gap-2.5 whitespace-nowrap">
                      <span className="text-[14px] font-medium text-ink">
                        {node.label}
                      </span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          startTopicPractice(node.id);
                        }}
                        className="px-4 py-2 bg-primary text-slate-900 text-[10px] font-medium rounded-xl hover:opacity-90 active:scale-95 transition-all"
                      >
                        Acessar Agora
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Practice Exercises View */}
      {viewState === 'practice' && (
        <div className="pt-8 px-6 max-w-md mx-auto flex flex-col justify-start h-full space-y-6">
          
          {/* Header row: back and progress bar */}
          {!showWelcomeScreen && !showTransitionScreen && !currentExerciseIntroType ? (
            <div className="flex items-center gap-4 animate-in fade-in duration-200">
              <button 
                onClick={() => { stopListening(); setViewState('roadmap'); }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-full text-slate-500 transition-colors"
              >
                <X size={20} />
              </button>
              {practiceStage === 'interleaved' ? (
                <>
                  <div className="flex-1 bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                    <div 
                      className="bg-ink h-full transition-all duration-300"
                      style={{ width: `${((currentIdx + 1) / (exercises.length || 10)) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">
                    {currentIdx + 1}/{exercises.length}
                  </span>
                </>
              ) : practiceStage === 'grammar' ? (
                grammarView === 'explanation' ? (
                  <>
                    <div className="flex-1 bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                      <div 
                        className="bg-ink h-full transition-all duration-300"
                        style={{ width: `${isGrammarUnavailable ? 100 : (((currentStepIdx + 1) / (explanationSteps.length || 1)) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">
                      {isGrammarUnavailable ? "1/1" : `${currentStepIdx + 1}/${explanationSteps.length}`}
                    </span>
                    {!isGrammarUnavailable && studyModeChoice === 'text' && (
                      <button
                        onClick={handleTranslatePage}
                        disabled={isTranslatingPage}
                        className={`flex items-center justify-center p-2 rounded-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 ${isPageTranslated ? 'bg-ink text-surface' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                        title={localT.translatePage}
                      >
                        {isTranslatingPage ? (
                          <Loader2 size={16} className="animate-spin text-primary" />
                        ) : (
                          <Languages size={16} />
                        )}
                      </button>
                    )}
                  </>
                ) : grammarView === 'quiz' ? (
                  <>
                    <div className="flex-1 bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                      <div 
                        className="bg-ink h-full transition-all duration-300"
                        style={{ width: `${((currentQuestionIdx) / (quizQuestions.length || 10)) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">
                      {currentQuestionIdx + 1}/{quizQuestions.length}
                    </span>
                    <button
                      onClick={handleToggleQuizTranslation}
                      disabled={isTranslatingQuiz}
                      className={`flex items-center justify-center p-2 rounded-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 ${isQuizTranslated ? 'bg-ink text-surface' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                      title={localT.translatePage || "Traduzir"}
                    >
                      {isTranslatingQuiz ? (
                        <Loader2 size={16} className="animate-spin text-primary" />
                      ) : (
                        <Languages size={16} />
                      )}
                    </button>
                  </>
                ) : null
              ) : (
                <>
                  <div className="flex-1 bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                    <div className="bg-ink h-full w-full" />
                  </div>
                  <span className="text-xs font-normal text-slate-500 uppercase font-mono tracking-[0.14em]">
                    1/1
                  </span>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center">
              <button 
                onClick={() => { stopListening(); setViewState('roadmap'); }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-full text-slate-500 transition-colors animate-in fade-in duration-200"
              >
                <X size={20} />
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <p className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]"><T k="ui.roadmap.loadingLesson" /></p>
            </div>
          ) : showWelcomeScreen ? (
            <div className="flex-grow flex flex-col justify-between space-y-6 pt-4 animate-in fade-in duration-300">
              <div className="bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <BookOpen size={32} />
                  </div>
                  <h2 className="text-2xl font-medium dark:text-ink leading-tight">
                    {localT.topicWelcomeTitle?.replace('{num}', currentTopicId?.replace('topic_', '') || '') || `Tópico ${currentTopicId?.replace('topic_', '')}`}
                  </h2>
                  <p className="text-sm font-medium text-slate-500 mt-2">
                    {currentTopicId === 'topic_3' || (parseInt(currentTopicId?.replace('topic_', '') || '0', 10) % 2 !== 0)
                      ? localT.topicWelcomeGrammarIntro
                      : localT.topicWelcomeIntro}
                  </p>
                </div>

                <div className="border-t border-black/[0.06] dark:border-slate-800/50 pt-4 space-y-3">
                  {(() => {
                    const topicNum = parseInt(currentTopicId?.replace('topic_', '') || '0', 10);
                    const list = [];
                    if (currentTopicId === 'topic_3') {
                      list.push({ text: localT.topicContainsGrammar, icon: <FileText className="text-tintd-lavender" size={16} /> });
                      list.push({ text: localT.topicContainsQuiz, icon: <Award className="text-warn" size={16} /> });
                    } else if (currentTopicId === 'topic_4') {
                      list.push({ text: localT.topicContainsWriting, icon: <FileText className="text-tintd-sky" size={16} /> });
                    } else if (topicNum % 2 !== 0) { // Odd topics
                      list.push({ text: localT.topicContainsFlashcards, icon: <Bookmark className="text-tintd-sky" size={16} /> });
                      list.push({ text: localT.topicContainsPronunciation, icon: <Mic className="text-ok" size={16} /> });
                      list.push({ text: localT.topicContainsGrammar, icon: <FileText className="text-tintd-lavender" size={16} /> });
                      list.push({ text: localT.topicContainsQuiz, icon: <Award className="text-warn" size={16} /> });
                      list.push({ text: localT.topicContainsWriting, icon: <FileText className="text-tintd-sky" size={16} /> });
                    } else { // Even topics
                      list.push({ text: localT.topicContainsFlashcards, icon: <Bookmark className="text-tintd-sky" size={16} /> });
                      list.push({ text: localT.topicContainsPronunciation, icon: <Mic className="text-ok" size={16} /> });
                      list.push({ text: localT.topicContainsWriting, icon: <FileText className="text-tintd-sky" size={16} /> });
                    }

                    return list.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-slate-700 dark:text-slate-300 font-semibold text-xs py-1">
                        <div className="w-7 h-7 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                          {item.icon}
                        </div>
                        <span>{item.text}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              <button
                onClick={handleConfirmWelcome}
                className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:scale-[1.01] active:scale-95 transition-all cursor-pointer"
              >
                {localT.confirm || "Confirmar"}
              </button>
            </div>
          ) : showTransitionScreen ? (
            <div className="flex-grow flex flex-col justify-between space-y-6 pt-4 animate-in fade-in duration-300">
              <div className="bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] flex flex-col items-center justify-center text-center gap-6 flex-grow">
                <div className="w-20 h-20 rounded-full bg-ok/10 text-ok flex items-center justify-center animate-bounce">
                  <Trophy size={40} className="fill-ok/10" />
                </div>
                <h3 className="text-xl font-medium text-ink tracking-[-0.02em] leading-tight px-4">
                  {activeTransitionPhrase}
                </h3>
              </div>

              <button
                onClick={() => {
                  setShowTransitionScreen(false);
                  if (onConfirmTransition) onConfirmTransition();
                }}
                className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all active:scale-[0.98] cursor-pointer"
              >
                {localT.confirm || "Confirmar"}
              </button>
            </div>
          ) : currentExerciseIntroType ? (
            <div className="flex-grow flex flex-col justify-between space-y-6 pt-4 animate-in fade-in duration-300">
              <div className="bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] flex flex-col items-center justify-center text-center gap-6 flex-grow">
                <div className="w-16 h-16 rounded-[24px] bg-primary/10 text-primary flex items-center justify-center">
                  {currentExerciseIntroType === 'flashcard' && <Bookmark size={32} />}
                  {currentExerciseIntroType === 'pronunciation' && <Mic size={32} />}
                  {currentExerciseIntroType === 'grammar' && <FileText size={32} />}
                  {currentExerciseIntroType === 'writing' && <FileText size={32} />}
                  {currentExerciseIntroType === 'activeVoice' && <Compass size={32} />}
                </div>
                <h3 className="text-sm font-medium text-ink text-primary">
                  {currentExerciseIntroType === 'flashcard' && (localT.topicContainsFlashcards || "Flashcards")}
                  {currentExerciseIntroType === 'pronunciation' && (localT.topicContainsPronunciation || "Pronúncia")}
                  {currentExerciseIntroType === 'grammar' && (localT.topicContainsGrammar || "Gramática")}
                  {currentExerciseIntroType === 'writing' && (localT.topicContainsWriting || "Escrita")}
                  {currentExerciseIntroType === 'activeVoice' && "Voz Ativa"}
                </h3>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 leading-relaxed px-2">
                  {(() => {
                    const cleanLang = (i18n.language || 'pt').split('-')[0].toLowerCase();
                    const trans = LOCAL_TRANSLATIONS[cleanLang] || LOCAL_TRANSLATIONS['pt'] || LOCAL_TRANSLATIONS['en'];
                    let intros = [];
                    if (currentExerciseIntroType === 'flashcard') intros = trans.flashcardIntros;
                    else if (currentExerciseIntroType === 'pronunciation') intros = trans.pronunciationIntros;
                    else if (currentExerciseIntroType === 'grammar') intros = trans.grammarIntros;
                    else if (currentExerciseIntroType === 'writing') intros = trans.writingIntros;
                    else if (currentExerciseIntroType === 'activeVoice') intros = trans.activeVoiceIntros;
                    
                    return intros?.[activeIntroIndex] || "Prepare-se para o exercício a seguir. Faça o seu melhor!";
                  })()}
                </p>
              </div>

              <button
                onClick={() => setCurrentExerciseIntroType(null)}
                className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:scale-[1.01] active:scale-95 transition-all cursor-pointer"
              >
                {localT.confirm || "Confirmar"}
              </button>
            </div>
          ) : practiceStage === 'grammar' ? (
            <div className="flex-1 flex flex-col justify-between">
              
              {/* VIEW: EXPLANATION */}
              {grammarView === 'explanation' && (
                <div className="flex-1 flex flex-col justify-between pt-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  {isGrammarUnavailable ? (
                    <div className="flex-grow flex flex-col justify-between space-y-6">
                      <div className="bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] flex flex-col items-center justify-center text-center gap-6 flex-grow">
                        <div className="w-16 h-16 rounded-[24px] bg-warn/10 text-warn flex items-center justify-center">
                          <AlertCircle size={32} />
                        </div>
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 leading-relaxed px-4">
                          {localT.grammarWorkingOnIt || "Estamos trabalhando nisso, o conteúdo será adicionado em breve."}
                        </h3>
                      </div>
                      
                      <button
                        disabled={isCompletingTopic}
                        onClick={async () => {
                          if (currentTopicId === 'topic_3') {
                            setIsCompletingTopic(true);
                          }
                          await nextExercise();
                        }}
                        className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50"
                      >
                        {isCompletingTopic && currentTopicId === 'topic_3' ? "Processando..." : (currentTopicId === 'topic_3' ? "Concluir Tópico 🎉" : "Continuar para Escrita ✍️")}
                      </button>
                    </div>
                  ) : explanationFetchError || explanationSteps.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center gap-6">
                      <div className="w-16 h-16 rounded-full bg-tint-rose dark:bg-tint-rose/30 flex items-center justify-center text-bad">
                        <AlertCircle size={32} />
                      </div>
                      <h3 className="text-sm font-semibold dark:text-ink leading-relaxed">{localT.errorLoading}</h3>
                    </div>
                  ) : studyModeChoice === 'select' ? (
                    <GrammarChoiceScreen
                      videoLocked={!GRAMMAR_VIDEO_LANGS.includes((langCode || 'en').toLowerCase())}
                      onSelectMode={(mode) => setStudyModeChoice(mode)}
                      titleText="Escolha como quer estudar gramática:"
                    />
                  ) : studyModeChoice === 'video' ? (
                    <GrammarVideoPlayer
                      langCode={langCode || 'en'}
                      level={level || 'a1'}
                      nodeId={getGrammarFileName(currentTopicId, (langCode || 'en').toLowerCase() === 'en')}
                      onFinishOrStartQuiz={() => {
                        triggerStreakUpdate('roadmap_grammar_explanation');
                        setGrammarView('quiz');
                        setSelectedWordInfo(null);
                        setCurrentQuestionIdx(0);
                        setTotalCorrect(0);
                        setTotalWrong(0);
                        setSelectedAnswer(null);
                        setShowFeedback(false);
                      }}
                    />
                  ) : (
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="space-y-6 flex-1 flex flex-col justify-center">
                        <div className="text-center text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">
                          Gramática - Explicação
                        </div>
                        <div className="w-full bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] space-y-4 max-h-[50vh] overflow-y-auto custom-scrollbar">
                          {/* Title */}
                          <h3 className="text-lg font-semibold text-ink leading-tight">
                            {isPageTranslated && translatedSteps[currentStepIdx]
                              ? translatedSteps[currentStepIdx].title
                              : explanationSteps[currentStepIdx]?.title}
                          </h3>
                          {/* Explanation Markdown */}
                          <div className="prose prose-slate dark:prose-invert prose-p:leading-relaxed text-slate-700 dark:text-slate-300 text-xs font-semibold">
                            <ReactMarkdown>
                              {isPageTranslated && translatedSteps[currentStepIdx]
                                ? translatedSteps[currentStepIdx].explanation
                                : explanationSteps[currentStepIdx]?.explanation || ''}
                            </ReactMarkdown>
                          </div>
                          {/* Tip Card */}
                          {(isPageTranslated && translatedSteps[currentStepIdx] ? translatedSteps[currentStepIdx].tip : explanationSteps[currentStepIdx]?.tip) && (
                            <div className="bg-primary/10 border border-primary/20 p-4 rounded-2xl mt-4">
                              <div className="flex items-center gap-2 text-primary font-semibold text-xs mb-1">
                                <Star size={16} fill="currentColor" />
                                <span>
                                  {isPageTranslated 
                                    ? localT.goldenTip 
                                    : (LOCAL_TRANSLATIONS[(langCode || 'en').toLowerCase()]?.goldenTip || LOCAL_TRANSLATIONS.en.goldenTip || "Golden Tip")
                                  }
                                </span>
                              </div>
                              <p className="font-semibold text-slate-700 dark:text-slate-300 leading-relaxed text-[11px]">
                                {isPageTranslated && translatedSteps[currentStepIdx]
                                  ? translatedSteps[currentStepIdx].tip
                                  : explanationSteps[currentStepIdx]?.tip}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Controls Footer */}
                      <div className="flex items-center gap-3 pt-6">
                        {currentStepIdx > 0 && (
                          <button
                            onClick={() => setCurrentStepIdx(prev => prev - 1)}
                            className="px-5 py-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl font-medium text-[10px] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 border border-slate-200/50 dark:border-slate-700/50"
                          >
                            <ChevronLeft size={16} /> {localT.back}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            // Contabiliza a streak ao avançar na explicação ou iniciar o quiz
                            triggerStreakUpdate('roadmap_grammar_explanation');
                            if (currentStepIdx < explanationSteps.length - 1) {
                              setCurrentStepIdx(prev => prev + 1);
                            } else {
                              setGrammarView('quiz');
                              setSelectedWordInfo(null);
                              setCurrentQuestionIdx(0);
                              setTotalCorrect(0);
                              setTotalWrong(0);
                              setSelectedAnswer(null);
                              setShowFeedback(false);
                            }
                          }}
                          className="text-[13px] font-medium flex-1 h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                          {currentStepIdx < explanationSteps.length - 1 ? (
                            <>{localT.next} <ArrowRight size={16} /></>
                          ) : (
                            <><Play size={16} fill="currentColor" /> {localT.startQuiz} 🚀</>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* VIEW: QUIZ */}
              {grammarView === 'quiz' && (
                <div className="flex-1 flex flex-col justify-between pt-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  {quizQuestions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="space-y-6 flex-1 flex flex-col justify-center">
                        <div className="text-center text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">
                          Quiz de Fixação
                        </div>
                        
                        <div className="w-full bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] space-y-6 relative grammar-quiz-container">
                          <h3 className="text-lg font-semibold text-ink leading-tight text-center">
                            {renderClickableTextForQuiz(
                              isQuizTranslated && translatedQuizPrompts[currentQuestionIdx]
                                ? translatedQuizPrompts[currentQuestionIdx]
                                : (quizQuestions[currentQuestionIdx]?.question_text || quizQuestions[currentQuestionIdx]?.question || '')
                            )}
                          </h3>

                          <div className="grid gap-3">
                            {(quizQuestions[currentQuestionIdx]?.options || []).map((opt: any, i: number) => {
                              const isSelected = selectedAnswer === opt.text;
                              const isRight = opt.is_correct;
                              
                              let btnClass = "bg-surface border border-black/[0.06] dark:border-white/[0.06] text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800";
                              
                              if (showFeedback) {
                                  if (isRight) {
                                    btnClass = "bg-ok/20 border-2 border-ok text-ok dark:text-ok";
                                  } else if (isSelected && !isRight) {
                                    btnClass = "bg-bad/20 border-2 border-bad text-bad dark:text-bad";
                                  }
                              } else if (isSelected) {
                                btnClass = "border-primary bg-tint-lavender dark:bg-tint-lavender/20 text-primary";
                              }

                              return (
                                <button
                                  key={i}
                                  disabled={showFeedback}
                                  onClick={() => setSelectedAnswer(opt.text)}
                                  className={`w-full py-4 px-6 rounded-2xl text-left text-xs font-semibold transition-all flex items-center justify-between cursor-pointer ${btnClass}`}
                                >
                                  <span>{opt.text}</span>
                                  {showFeedback && isRight && <Check size={16} className="text-ok" />}
                                  {showFeedback && isSelected && !isRight && <X size={16} className="text-bad" />}
                                </button>
                              );
                            })}
                          </div>

                          {showFeedback && selectedAnswer && (
                            (() => {
                              const currentQ = quizQuestions[currentQuestionIdx];
                              const selectedOpt = currentQ?.options?.find((o: any) => o.text === selectedAnswer);
                              const correct = !!selectedOpt?.is_correct;
                              if (!correct && currentQ?.wrong_explanation) {
                                return (
                                  <div className="mt-4 p-4 bg-tint-rose dark:bg-tint-rose/20 border border-tint-rose dark:border-tint-rose/30 rounded-2xl animate-in slide-in-from-bottom-2">
                                    <h4 className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] mb-1">
                                      {localT.errorExplanation || "Explicação do erro:"}
                                    </h4>
                                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 leading-relaxed">
                                      {isQuizTranslated && translatedQuizExplanations[currentQuestionIdx]
                                        ? translatedQuizExplanations[currentQuestionIdx]
                                        : currentQ.wrong_explanation
                                      }
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            })()
                          )}

                          {/* Word Translation Bubble */}
                          {selectedWordInfo && (
                            <div 
                              onClick={(e) => e.stopPropagation()}
                              className="absolute z-[80] bg-ink text-surface px-3 py-2 rounded-xl text-xs font-semibold select-none pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
                              style={{ 
                                left: `${selectedWordInfo.x}px`, 
                                top: `${selectedWordInfo.y}px`,
                                transform: 'translate(-50%, -100%)'
                              }}
                            >
                              {selectedWordInfo.loading ? (
                                <div className="flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin text-primary" />
                                  <span>{translatingLabel}</span>
                                </div>
                              ) : (
                                <span>{selectedWordInfo.translation}</span>
                              )}
                              <div 
                                className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-900 dark:border-t-white"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="pt-6">
                        {!showFeedback ? (
                          <button
                            disabled={!selectedAnswer}
                            onClick={() => {
                              // Contabiliza a streak ao verificar resposta de questão do quiz
                              triggerStreakUpdate('roadmap_quiz');
                              const currentQ = quizQuestions[currentQuestionIdx];
                              const selectedOpt = currentQ.options.find((o: any) => o.text === selectedAnswer);
                              const correct = !!selectedOpt?.is_correct;
                              import('../services/soundService').then(({ soundService }) => soundService.play(correct ? 'success' : 'error'));
                              
                              const newTotalCorrect = totalCorrect + (correct ? 1 : 0);
                              const newTotalWrong = totalWrong + (correct ? 0 : 1);
                              
                              if (user) {
                                const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
                                const quizItemId = currentQ.question_id || `quiz_${currentTopicId || 't1'}_${currentQuestionIdx}`;
                                const rawQuestionStr = currentQ.question || currentQ.question_text || currentQ.prompt || currentQ.sentence || currentQ.title || currentQ.text || 'Pergunta de Quiz';
                                const optionTexts = Array.isArray(currentQ.options) ? currentQ.options.map((o: any) => o.text || '').join(' ') : '';
                                const qText = `${rawQuestionStr} ${optionTexts}`;
                                
                                import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
                                  uniqueWordsService.recordExerciseCompletion(
                                    user.id,
                                    cleanLang,
                                    'quizzes',
                                    quizItemId,
                                    qText
                                  );
                                });
                              }

                              if (correct) {
                                setTotalCorrect(newTotalCorrect);
                              } else {
                                setTotalWrong(newTotalWrong);
                                if (user) {
                                  const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
                                  const quizItemId = currentQ.question_id || `quiz_${currentTopicId || 't1'}_${currentQuestionIdx}`;
                                  const rawQuestionStr = currentQ.question || currentQ.question_text || currentQ.prompt || currentQ.sentence || currentQ.title || currentQ.text || 'Pergunta de Quiz';
                                  const quizPayload = {
                                    question: rawQuestionStr,
                                    question_text: rawQuestionStr,
                                    options: (currentQ.options || []).map((o: any) => typeof o === 'string' ? o : (o.text || JSON.stringify(o))),
                                    correctAnswer: (currentQ.options || []).find((o: any) => o.is_correct)?.text || currentQ.correctAnswer || currentQ.options?.[0]?.text || currentQ.options?.[0],
                                    explanation: currentQ.wrong_explanation || currentQ.explanation || ''
                                  };

                                  import('../services/errorReviewService').then(({ errorReviewService }) => {
                                    errorReviewService.recordError({
                                      userId: user.id,
                                      language: cleanLang,
                                      exerciseType: 'quizzes',
                                      sourceOrigin: 'roadmap',
                                      itemId: quizItemId,
                                      itemPayload: quizPayload,
                                      userInput: selectedAnswer
                                    });
                                  });
                                }
                              }
                              setShowFeedback(true);

                              // Log stats and save to D1 database
                              if (user) {
                                const cleanLang = (langCode || 'en').split('-')[0].toLowerCase();
                                const nowStr = getLocalISOString();
                                
                                import('../services/statsService').then(({ statsService }) => {
                                  statsService.increment('total_quizzes_attempted', user.id, 1, { langCode: cleanLang });
                                  if (correct) {
                                    statsService.increment('total_correct_answers' as any, user.id, 1, { langCode: cleanLang });
                                  } else {
                                    statsService.increment('total_wrong_answers' as any, user.id, 1, { langCode: cleanLang });
                                  }
                                });

                                import('../services/db_manager').then(({ dbManager }) => {
                                  dbManager.execute({
                                    action: 'save_quiz_progress' as any,
                                    table: 'quiz_progress',
                                    id: quizSessionId,
                                    target_language: langCode === 'fr' ? 'french' : langCode === 'it' ? 'italian' : NEW_GRAMMAR_LANGUAGES[langCode || ''] ? langCode : 'english',
                                    language_level: level || 'a1',
                                    total_correct: newTotalCorrect,
                                    total_wrong: newTotalWrong,
                                    recorded_at: nowStr
                                  } as any).catch(err => console.error("Failed to save progress:", err));

                                  if (!correct) {
                                    dbManager.execute({
                                      action: 'save_quiz_wrong_answers' as any,
                                      table: 'quiz_wrong_answers',
                                      wrong_answers: [{
                                        id: crypto.randomUUID(),
                                        quiz_session_id: quizSessionId,
                                        question_id: currentQ.question_id,
                                        recorded_at: nowStr
                                      }]
                                    } as any).catch(err => console.error("Failed to save wrong answer:", err));
                                  }
                                });
                              }
                            }}
                            className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-95 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50"
                          >
                            Verificar Resposta
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedWordInfo(null);
                              if (currentQuestionIdx < quizQuestions.length - 1) {
                                setCurrentQuestionIdx(prev => prev + 1);
                                setSelectedAnswer(null);
                                setShowFeedback(false);
                              } else {
                                setGrammarView('result');
                              }
                            }}
                            className="text-[13px] font-medium w-full h-12 bg-ok text-surface rounded-full hover:opacity-95 transition-all active:scale-[0.98] cursor-pointer"
                          >
                            Continuar
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* VIEW: RESULT */}
              {grammarView === 'result' && (
                <div className="flex-1 flex flex-col justify-between pt-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="space-y-6 flex-1 flex flex-col justify-center">
                    <div className="text-center text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">
                      Resultado do Quiz
                    </div>
                    
                    <div className="w-full bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 rounded-[28px] flex flex-col items-center text-center gap-6">
                      <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Trophy size={40} />
                      </div>
                      
                      <div className="space-y-2">
                        <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">
                          {totalCorrect >= 7 ? localT.excellentResult : localT.keepPracticingResult}
                        </h3>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 leading-relaxed">
                          {localT.correctAnswersCount}: {totalCorrect} de {quizQuestions.length}
                        </p>
                      </div>

                      {totalCorrect < 7 && (
                        <p className="text-xs text-bad font-bold max-w-xs leading-relaxed">
                          {localT.minScoreWarning}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="pt-6">
                    {totalCorrect >= 7 ? (
                      <button
                        disabled={isCompletingTopic}
                        onClick={async () => {
                          if (currentTopicId === 'topic_3') {
                            setIsCompletingTopic(true);
                          }
                          await nextExercise();
                        }}
                        className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCompletingTopic && currentTopicId === 'topic_3' ? "Processando..." : (currentTopicId === 'topic_3' ? "Concluir Tópico 🎉" : "Continuar para Escrita ✍️")}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setGrammarView('explanation');
                          setCurrentStepIdx(0);
                        }}
                        className="text-[13px] font-medium w-full h-12 bg-bad text-white rounded-full hover:opacity-95 transition-all active:scale-[0.98] cursor-pointer"
                      >
                        {localT.tryAgain} 🔄
                      </button>
                    )}
                  </div>
                </div>
              )}

            </div>
          ) : practiceStage === 'writing' ? (
            <div className="flex-1 flex flex-col justify-between pt-4 animate-in fade-in slide-in-from-right-4 duration-300">
              {!writingStory ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="flex-grow flex flex-col justify-between space-y-6">
                  
                  <div className="relative writing-container bg-surface rounded-[28px] p-6 border border-slate-200/50 dark:border-slate-800/80 space-y-4">
                    <h2 className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">
                      Texto Original para Traduzir
                    </h2>
                    <h3 className="text-lg font-semibold dark:text-ink leading-tight">
                      {writingStory.title || writingStory.titulo}
                    </h3>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 italic">
                      "{renderClickableText(writingStory.texto)}"
                    </p>

                    {/* Word Translation Bubble */}
                    {selectedWordInfo && (
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        className="absolute z-[80] bg-ink text-surface px-3 py-2 rounded-xl text-xs font-semibold select-none pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
                        style={{ 
                          left: `${selectedWordInfo.x}px`, 
                          top: `${selectedWordInfo.y}px`,
                          transform: 'translate(-50%, -100%)'
                        }}
                      >
                        {selectedWordInfo.loading ? (
                          <div className="flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin text-primary" />
                            <span>{translatingLabel}</span>
                          </div>
                        ) : (
                          <span>{selectedWordInfo.translation}</span>
                        )}
                        <div 
                          className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-900 dark:border-t-white"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <textarea
                      value={writingUserInput}
                      onChange={(e) => setWritingUserInput(e.target.value)}
                      placeholder={i18nInstance.t('ui.fc.typeTranslation')}
                      className="w-full h-32 bg-surface rounded-2xl p-5 border border-black/[0.06] focus:border-primary outline-none font-medium text-sm dark:text-ink resize-none"
                    />
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          const isProUser = profile && (profile.plan_type === 'pro' || profile.subscription_tier === 'pro');
                          if (!isProUser && roadmapWritingCount >= 5) {
                            setShowLimitModal(true);
                            return;
                          }
                          handleWritingAICorrect();
                        }}
                        disabled={isCorrectingWriting || !writingUserInput.trim()}
                        className="text-[13px] font-medium flex-1 h-12 bg-primary text-slate-900 rounded-full hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {isCorrectingWriting ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                        {isCorrectingWriting ? 'Corrigindo...' : 'Corrigir'}
                      </button>

                      <button
                        onClick={nextExercise}
                        disabled={isCorrectingWriting}
                        className="py-4 px-6 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl font-medium text-[13px] hover:bg-slate-300 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                      >
                        <SkipForward size={18} />
                        {localT.skip || "Pular"}
                      </button>
                    </div>
                    {(() => {
                      const isProUser = profile && (profile.plan_type === 'pro' || profile.subscription_tier === 'pro');
                      if (!isProUser) {
                        return (
                          <div className="text-[10px] text-slate-400 dark:text-slate-500 font-bold text-center mt-1">
                            {localT.freeCorrectionsLabel?.replace('{count}', roadmapWritingCount.toString()) || `Correções gratuitas diárias: ${roadmapWritingCount}/5`}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {writingFeedback && (
                    <div className="bg-surface rounded-[28px] p-6 border border-ok/20 space-y-4 animate-in slide-in-from-right-4 max-h-[35vh] overflow-y-auto custom-scrollbar">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="text-ok" size={20} />
                          <h2 className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok"><T k="ui.roadmap.expert" /></h2>
                        </div>
                        <div className="flex items-center gap-2">
                          {user && (
                            <button
                              onClick={handleSaveWritingCorrection}
                              disabled={isSavingWritingCorrection || savedWritingCorrectionSuccess}
                              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400 disabled:opacity-50"
                              title={i18nInstance.t('writing_page.save_correction')}
                            >
                              {isSavingWritingCorrection ? (
                                <Loader2 size={16} className="animate-spin text-ok" />
                              ) : savedWritingCorrectionSuccess ? (
                                <Bookmark size={16} className="fill-ok text-ok" />
                              ) : (
                                <Bookmark size={16} />
                              )}
                            </button>
                          )}
                          <button 
                            onClick={() => setWritingFeedback(null)} 
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400" 
                            title="Tentar novamente"
                          >
                            <RotateCcw size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="prose dark:prose-invert prose-xs max-w-none text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-medium leading-relaxed text-xs">
                        {writingFeedback}
                      </div>
                    </div>
                  )}

                  {writingFeedback && (
                    <button
                      disabled={isCompletingTopic}
                      onClick={async () => {
                        setIsCompletingTopic(true);
                        await nextExercise();
                      }}
                      className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCompletingTopic ? 'Processando...' : 'Concluir Tópico 🎉'}
                    </button>
                  )}

                </div>
              )}
            </div>
          ) : exercises[currentIdx] ? (
            <div className="flex-1 flex flex-col justify-between">
              
              {/* Flashcard Practice Card */}
              {exercises[currentIdx].type === 'flashcard' && (
                <div className="space-y-6 flex-1 flex flex-col justify-center">
                  <div className="flex items-center justify-between px-2">
                    <div className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary">
                      Flashcard - Pratique a Tradução
                    </div>
                    <button
                      type="button"
                      onClick={toggleSwapDeck}
                      className={`p-2 rounded-xl border transition-all flex items-center gap-1.5 cursor-pointer text-xs font-bold ${
                        isDeckSwapped 
                          ? 'bg-primary/20 border-primary text-primary dark:text-primary-light' 
                          : 'bg-slate-100 dark:bg-slate-800 border-black/[0.06] dark:border-slate-700 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                      title={i18nInstance.t('ui.fc.flip')}
                    >
                      <ArrowLeftRight size={14} />
                      <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] hidden sm:inline">
                        {isDeckSwapped ? 'Invertido' : 'Normal'}
                      </span>
                    </button>
                  </div>
                  
                  <div 
                    onClick={() => setSelectedWordInfo(null)}
                    className="w-full aspect-[4/3] rounded-[28px] bg-surface border border-slate-200/50 dark:border-slate-800/80 p-8 flex flex-col items-center justify-center text-center relative flashcard-inner-container"
                  >
                    <div className="absolute inset-0 rounded-[28px] overflow-hidden pointer-events-none">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    </div>
                    
                    {!cardFlipped ? (
                      <div className="space-y-6 relative z-10 w-full">
                        <div className="text-slate-400 text-xs font-normal uppercase font-mono tracking-[0.14em] select-none"><T k="ui.roadmap.whatTranslation" /></div>
                        <h2 className="text-2xl font-medium dark:text-ink leading-snug px-4 select-none">
                          {isDeckSwapped 
                            ? renderClickableText(exercises[currentIdx].translation, true)
                            : renderClickableText(exercises[currentIdx].original, false)
                          }
                        </h2>
                        <div className="flex justify-center gap-2">
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              const textToSpeak = isDeckSwapped ? exercises[currentIdx].translation : exercises[currentIdx].original;
                              const langToSpeak = isDeckSwapped ? (i18n.language || 'pt').split('-')[0].toLowerCase() : (langCode || 'en').split('-')[0].toLowerCase();
                              textToSpeechService.speak(textToSpeak, langToSpeak, 0.95);
                            }}
                            className="p-3 bg-primary/10 text-primary hover:bg-ink hover:text-white rounded-full transition-all active:scale-95 cursor-pointer"
                          >
                            <Volume2 size={20} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6 animate-in fade-in duration-300 relative z-10">
                        <div className="text-ok dark:text-ok text-xs font-normal uppercase font-mono tracking-[0.14em]"><T k="ui.roadmap.correctTranslation" /></div>
                        <h2 className="text-2xl font-medium text-ok dark:text-ok leading-snug px-4">
                          {isDeckSwapped 
                            ? renderClickableText(exercises[currentIdx].original, false)
                            : exercises[currentIdx].translation
                          }
                        </h2>
                      </div>
                    )}

                    {/* Word Translation Bubble */}
                    {selectedWordInfo && (
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        className="absolute z-[80] bg-ink text-surface px-3 py-2 rounded-xl text-xs font-semibold select-none pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
                        style={{ 
                          left: `${selectedWordInfo.x}px`, 
                          top: `${selectedWordInfo.y}px`,
                          transform: 'translate(-50%, -100%)'
                        }}
                      >
                        {selectedWordInfo.loading ? (
                          <div className="flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin text-primary" />
                            <span>{translatingLabel}</span>
                          </div>
                        ) : (
                          <span>{selectedWordInfo.translation}</span>
                        )}
                        <div 
                          className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-900 dark:border-t-white"
                        />
                      </div>
                    )}
                  </div>

                   {!cardFlipped ? (
                    <button 
                      onClick={() => setCardFlipped(true)}
                      className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-95 transition-all active:scale-[0.98] cursor-pointer"
                    >
                      Revelar Tradução
                    </button>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 w-full animate-in fade-in zoom-in-95 duration-200">
                      {[
                        { key: 'again', label: 'De novo', color: 'bg-bad hover:bg-bad', shadow: '' },
                        { key: 'hard', label: 'Difícil', color: 'bg-warn hover:bg-warn', shadow: '' },
                        { key: 'good', label: 'Bom', color: 'bg-ok hover:bg-ok', shadow: '' },
                        { key: 'easy', label: 'Fácil', color: 'bg-tintd-sky hover:bg-tintd-sky', shadow: '' }
                      ].map((btn) => (
                        <button 
                          key={btn.key}
                          onClick={() => handleFlashcardRating(btn.key as any)} 
                          className={`py-4 ${btn.color} text-white rounded-2xl font-normal text-xs uppercase font-mono tracking-[0.14em] active:scale-95 transition-all ${btn.shadow} cursor-pointer`}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Pronunciation Practice Card */}
              {exercises[currentIdx].type === 'pronunciation' && (
                <div className="space-y-8 flex-1 flex flex-col justify-center animate-in fade-in zoom-in-95 duration-300">
                  <div 
                    className={`w-full bg-surface p-10 rounded-[28px] border-4 transition-all duration-500 flex flex-col items-center text-center gap-6 ${
                      recognitionFeedback === 'success' ? 'border-ok scale-[1.02]' :
                      recognitionFeedback === 'error' ? 'border-bad' :
                      'border-transparent dark:border-white/[0.06]'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ink opacity-60 mb-2">
                        OUVIR E REPETIR
                      </div>
                      {recognitionFeedback === 'success' && <Trophy className="text-ok animate-bounce" size={32} />}
                    </div>

                    <div className="flex flex-col items-center">
                      <h2 className="text-2xl md:text-3xl font-medium dark:text-ink leading-tight">
                        {exercises[currentIdx].original}
                      </h2>
                    </div>

                    {recognitionFeedback === 'success' ? (
                      <p className="text-ok dark:text-ok text-sm font-medium -mt-2 animate-in fade-in zoom-in-95">
                        {exercises[currentIdx].translation}
                      </p>
                    ) : (
                      <p className="text-slate-400 dark:text-slate-500 text-sm font-medium -mt-2">
                        {exercises[currentIdx].translation}
                      </p>
                    )}

                    {/* Speed and recitation controls */}
                    <div className="flex flex-col items-center gap-4">
                      <div className="flex items-center gap-6">
                        <button
                          onClick={() => setTtsRate(r => Math.max(0.4, r - 0.1))}
                          className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-400 hover:text-primary transition-all active:scale-90"
                          title="Diminuir velocidade"
                        >
                          <Minus size={20} />
                        </button>

                        <button
                          onClick={() => handleSpeakWithRate(exercises[currentIdx].original, langCode || 'en')}
                          className={`p-5 rounded-full transition-all active:scale-95 ${
                            isTtsPlaying ? 'bg-bad text-surface' : 'bg-primary/10 dark:bg-primary/20 text-primary hover:scale-105'
                          }`}
                        >
                          {isTtsPlaying ? <Square size={28} fill="currentColor" /> : <Play size={28} />}
                        </button>

                        <button
                          onClick={() => setTtsRate(r => Math.min(2.0, r + 0.1))}
                          className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-400 hover:text-primary transition-all active:scale-90"
                          title="Aumentar velocidade"
                        >
                          <Plus size={20} />
                        </button>
                      </div>
                      <div className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-ink opacity-50">
                        VELOCIDADE: {ttsRate.toFixed(1)}x
                      </div>
                    </div>

                    <div className="w-full min-h-[40px] flex flex-col items-center justify-center">
                      {isListening && (
                        <span className="text-[10px] font-normal text-primary uppercase font-mono tracking-[0.14em] animate-pulse">Gravando voz...</span>
                      )}
                      {!isListening && lastTranscript && (
                        <p className="text-xs italic text-slate-500 dark:text-slate-400">
                          Você disse: "{lastTranscript}"
                        </p>
                      )}
                      {recognitionFeedback === 'success' && (
                        <span className="text-xs font-normal text-ok uppercase font-mono tracking-[0.14em] mt-1"><T k="ui.roadmap.greatPron" /></span>
                      )}
                      {recognitionFeedback === 'error' && (
                        <span className="text-xs font-normal text-bad uppercase font-mono tracking-[0.14em] mt-1">Tente novamente</span>
                      )}
                    </div>
                  </div>

                  {/* Bottom Controls matching /pronunciation (Pular center, Mic right - Image 5) */}
                  <div className="flex items-center justify-center gap-12 w-full px-4 pt-4">
                    {/* Pular button */}
                    <button 
                      onClick={nextExercise}
                      className="flex flex-col items-center gap-2 group transition-all duration-200 active:scale-95 cursor-pointer"
                    >
                      <div className="w-14 h-14 rounded-full border border-slate-700 bg-slate-900/60 text-slate-400 flex items-center justify-center group-hover:text-white group-hover:border-slate-500 transition-colors">
                        <SkipForward size={20} />
                      </div>
                      <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 group-hover:text-slate-300">
                        Pular
                      </span>
                    </button>

                    {/* Mic Recording Button (Displays 15s timer when active!) */}
                    <button 
                      onClick={() => toggleListening(exercises[currentIdx].original)}
                      className={`w-20 h-20 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer relative ${
                        isListening 
                          ? 'bg-bad ring-8 ring-bad/30 scale-105' 
                          : 'bg-bad hover:scale-105 active:scale-95'
                      }`}
                    >
                      {isListening ? (
                        <>
                          <div className="absolute -inset-2 rounded-full bg-bad/40 animate-ping pointer-events-none" />
                          <span className="text-sm font-semibold text-white">{timeLeft}s</span>
                        </>
                      ) : (
                        <Mic size={32} />
                      )}
                    </button>
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]"><T k="ui.roadmap.noExercise" /></p>
            </div>
          )}

        </div>
      )}

      {/* Completion Congratulatory Dialog */}
      {showCompletionModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-md text-center space-y-6 relative overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/5 rounded-full" />
            
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-[24px] bg-tint-butter dark:bg-tint-butter/20 text-warn flex items-center justify-center animate-bounce">
                <Trophy size={40} className="fill-warn/10" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-medium dark:text-ink tracking-[-0.03em]">{localT.congratsTitle}</h2>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold leading-relaxed px-2">
                {localT.congratsText?.replace('{num}', currentTopicId ? currentTopicId.replace('topic_', '') : '1')}
              </p>
            </div>

            <button 
              onClick={() => setShowCompletionModal(false)}
              className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all active:scale-[0.98] cursor-pointer"
            >
              {localT.uauContinue}
            </button>
          </div>
        </div>
      )}

      {/* Free Tier Writing Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-md text-center space-y-6 relative overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-[24px] bg-tint-rose dark:bg-tint-rose/20 text-bad flex items-center justify-center">
                <AlertCircle size={32} />
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-xl font-medium text-ink tracking-[-0.02em]">
                {localT.premiumLimitTitle || "Limite atingido"}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold leading-relaxed px-2">
                {localT.premiumLimitMessage || "Você atingiu o limite de correções diárias. Faça upgrade para o LibriTui Premium para correções ilimitadas com o Professor!"}
              </p>
            </div>

            <button 
              onClick={() => setShowLimitModal(false)}
              className="text-[13px] font-medium w-full h-12 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full hover:bg-slate-300 dark:hover:bg-slate-700 transition-all cursor-pointer"
            >
              {localT.close || "Fechar"}
            </button>
          </div>
        </div>
      )}

      {/* Bonus Active Voice Modal */}
      {selectedBonusDef && (
        <BonusActiveVoiceModal
          isOpen={isBonusModalOpen}
          onClose={() => setIsBonusModalOpen(false)}
          level={level || 'a1'}
          targetLangCode={langCode || 'en'}
          nativeLangCode={i18n.language || 'pt'}
          bonusDef={selectedBonusDef}
        />
      )}

      {/* Blocked Topic Modal */}
      {showBlockedTopicModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-md text-center space-y-6 relative overflow-hidden animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setShowBlockedTopicModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 rounded-full cursor-pointer transition-colors"
            >
              <X size={20} />
            </button>

            <div className="flex justify-center pt-2">
              <div className="w-16 h-16 rounded-3xl bg-tint-butter dark:bg-tint-butter/30 text-warn dark:text-warn flex items-center justify-center ring-4 ring-warn/20">
                <Lock size={32} />
              </div>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em] tracking-tight">
                {blockedTopicModalContent.title}
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm font-semibold leading-relaxed px-2">
                {blockedTopicModalContent.message}
              </p>
            </div>

            <button 
              onClick={() => setShowBlockedTopicModal(false)}
              className="text-[13px] font-medium w-full h-12 bg-primary hover:opacity-90 text-slate-900 rounded-full transition-all active:scale-[0.98] cursor-pointer"
            >
              {localT.confirm || "Confirmar"}
            </button>
          </div>
        </div>
      )}

      {/* Translation Error Notice Modal */}
      {showTranslationErrorModal && (
        <div className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-sm w-full text-center space-y-4 animate-in zoom-in-95">
            <div className="w-12 h-12 rounded-full bg-warn/20 text-warn flex items-center justify-center mx-auto">
              <AlertCircle size={24} />
            </div>
            <div className="space-y-1">
              <h4 className="text-base font-bold text-white"><T k="ui.roadmap.noTranslation" /></h4>
              <p className="text-xs text-slate-300">
                Não foi possível traduzir a página no momento. Tente novamente em alguns instantes.
              </p>
            </div>
            <button
              onClick={() => setShowTranslationErrorModal(false)}
              className="w-full py-3 bg-primary hover:bg-primary/90 text-slate-900 rounded-xl font-normal text-xs uppercase font-mono tracking-[0.14em] transition-all cursor-pointer"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {/* PRO Notice Modal for Free Users */}
      {showProNoticeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-surface p-8 rounded-[28px] w-full max-w-md animate-in zoom-in-95 relative border border-black/[0.06] dark:border-white/[0.06]">
            <button 
              onClick={() => setShowProNoticeModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 rounded-full cursor-pointer"
            >
              <X size={20} />
            </button>

            <div className="w-16 h-16 rounded-3xl mb-6 flex items-center justify-center bg-tint-peach text-primary mx-auto ring-4 ring-primary/15/30">
              <Sparkles size={32} />
            </div>
            
            <h3 className="text-xl font-semibold text-center text-ink mb-4 leading-snug">
              {PRO_REQUIRED_MESSAGES[currentNativeLang]?.title || PRO_REQUIRED_MESSAGES['pt'].title}
            </h3>
            
            <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm mb-8 text-center leading-relaxed font-semibold">
              {proModalMessage}
            </p>

            <div className="flex flex-col gap-3">
              <button 
                onClick={() => {
                  setShowProNoticeModal(false);
                  navigate('/settings');
                }} 
                className="text-[13px] font-medium w-full h-12 bg-primary hover:opacity-90 text-slate-900 rounded-full transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
              >
                <Sparkles size={16} />
                {PRO_REQUIRED_MESSAGES[currentNativeLang]?.btn || PRO_REQUIRED_MESSAGES['pt'].btn}
              </button>
              <button 
                onClick={() => setShowProNoticeModal(false)} 
                className="text-[13px] font-medium w-full h-12 bg-tint-stone/60 text-muted hover:text-ink dark:text-slate-400 rounded-full hover:bg-tint-stone transition-colors cursor-pointer"
              >
                {PRO_REQUIRED_MESSAGES[currentNativeLang]?.close || PRO_REQUIRED_MESSAGES['pt'].close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Level Completion & 5 Book Recommendation Modal */}
      <LevelCompletionModal
        isOpen={showLevelCompletionModal}
        onClose={() => setShowLevelCompletionModal(false)}
        level={level || 'A1'}
        targetLangCode={langCode || 'en'}
        nativeLangCode={i18n.language || 'pt'}
        onContinueStudies={handleContinueStudies}
      />
    </div>
  );
}
